/* Batcher Cockpit · v4.4 — zero-scroll monitoring layout.
   Left 50%: plant graphics + high-contrast silo/bin gauges. Right 50%: truck queue (top) +
   live batch weights with Rinstrum streaming into the ACTUAL column (bottom). */
import { Fragment, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { speakLoader, getLoaderVoiceOn } from "@/lib/voice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Activity, Pencil, Lock, OctagonAlert, CheckCircle2, Truck, ArrowRight, Upload, Timer, Scissors, Play, MapPin, Scale } from "lucide-react";
import { metaFor } from "@/lib/materials";
import PlantLiveBoard from "@/components/PlantLiveBoard";
import { LANES, SILOS, scaleForKey, MATERIAL_ORDER, ADDITIVE_KEYS, STAGES, loadTruckAt, dev, pct, aggTargetTotal, aggKeysFor, BIN_EMPTY_KG, BIN_NO } from "@/lib/batching";
import { speakLoadTruck, speakLoadComplete, tone } from "@/lib/voice";


// The Rinstrum panel streams ACTION records (DUMP while the bin empties into the
// truck, plus TARE/ZERO/TEST) down the same port as real weigh records. Their weight
// is not a material being batched, so the material rows must ignore them. The backend
// flags them, and we re-derive it from the label so an older agent still behaves.
const ACTION_LABELS = new Set(["dump", "tare", "zero", "test"]);
/* Call-out ladder for a bin: three round numbers scaled to the target, so a 5925 kg
   agg bin says "six hundred… two fifty… one hundred" and a 345 kg one says
   "thirty five… fifteen… five". */
const roundCall = (v) =>
  v >= 400 ? Math.round(v / 100) * 100
    : v >= 100 ? Math.round(v / 50) * 50
      : v >= 40 ? Math.round(v / 10) * 10
        : Math.max(5, Math.round(v / 5) * 5);
export const calloutSteps = (target) =>
  [...new Set([0.10, 0.04, 0.02].map((f) => roundCall(target * f)))].filter((n) => n > 0).sort((a, b) => b - a);

/* The Rinstrum panel names the product it is weighing ("WHITE PEBBLE"), but only the
   standard bins (20MM, SAND…) come back from the bridge with a material KEY. Custom and
   exposed-aggregate rows therefore arrived unnamed, so no row would take the live weight
   and nothing got recorded. Match the panel's own label against the row labels instead. */
const slugOf = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9]/g, "");
export function materialKeyOf(reading, materials) {
  if (!reading) return "";
  const mats = materials || [];
  // Trust an explicit material code ONLY when it matches a row we actually have.
  // Standard bins stream their real key (e.g. "agg_7mm") and lock straight away.
  // But custom/exposed products (ICE ROCK, WHITE DUST…) often stream a free-text
  // code that is NOT one of our keys — returning it verbatim matched no row, so
  // the reading was silently discarded and the row never recorded. When the code
  // is unknown, fall through to name matching below (label OR the code itself).
  if (reading.material && (!mats.length || mats.some((m) => m.key === reading.material))) {
    return reading.material;
  }
  const L = slugOf(reading.label) || slugOf(reading.material);
  if (!L || L === "total") return "";
  for (const m of mats) {
    if (slugOf(m.meta?.label) === L || slugOf(m.key).replace(/^exposed/, "") === L) return m.key;
  }
  for (const m of mats) {
    const ml = slugOf(m.meta?.label);
    if (ml && (ml.includes(L) || L.includes(ml))) return m.key;
  }
  return "";
}

export const isActionReading = (r) => {
  if (!r) return false;
  if (r.action != null) return !!r.action;
  return ACTION_LABELS.has(String(r.label || "").toLowerCase().replace(/[^a-z0-9]/g, ""));
};

/* ── THE BATCHING SEQUENCE ────────────────────────────────────────────────────
   One hook drives the whole workflow off the AGGREGATE weigher, because that is
   the only live signal at this plant (water/cement/additives are keyed by hand).

     step 1 PREP        → operator keys water + cement + additives
     step 2 LOADING     → bin filling
     step 3 LOAD TRUCK  → bin has reached the SUM of the agg targets  (green flag + voice)
     step 4 DISCHARGING → operator pressed LOAD TRUCK, bin emptying
     step 5 COMPLETE    → bin back to 0 kg ±50  (voice + docket + truck → slumping)

   ANTI-FREEZE RULES (the operator's #1 complaint):
   · NOTHING in here waits on the panel's `stable`/motion flag.
   · Every transition is LATCHED, so a dropped poll, a stale reading or the panel
     going quiet after the gate shuts can never roll the sequence backwards.
   · The DUMP record IS used here (it is the bin contents falling away) even though
     the material rows ignore it.
──────────────────────────────────────────────────────────────────────────────── */
export function useAggSequence(batch, scaleFeed, tolerance = 1, isOpenKey = null) {
  const target = aggTargetTotal(batch);
  const r = scaleFeed?.online ? scaleFeed.scaleById?.agg : null;
  const live = r && !r.stale && r.weight_kg != null ? r.weight_kg : null;
  const loadedAt = loadTruckAt(batch);
  const complete = !!batch?.ready_at;

  // STEP 3 — every aggregate material has made weight and locked. A row that was
  // auto-locked UNDER target stays OPEN (the loader is going back to the same bin for
  // more), so it does not count as reached — that is what used to freeze the sequence.
  const aggKeys = aggKeysFor(batch).filter((k) => (batch?.target_weights?.[k] || 0) > 0);
  const reached = complete || !!loadedAt
    || (aggKeys.length > 0 && aggKeys.every((k) => (batch?.actual_weights?.[k] || 0) > 0 && !isOpenKey?.(k)));

  // STEP 4→5 — the operator's button click IS the "bin is discharging" signal. From
  // that moment the only job is to wait for the agg scale to RETURN to zero. No DUMP
  // flag, no discharge status, no stability check.
  const empty = live != null && Math.abs(live) <= BIN_EMPTY_KG;
  const armed = useRef(false);
  const dischargedLatch = useRef(false);
  const emptySince = useRef(null);
  const forBatch = useRef(null);
  if (forBatch.current !== (batch?.id || null)) {
    forBatch.current = batch?.id || null;
    armed.current = false;
    dischargedLatch.current = false;
    emptySince.current = null;
  }
  // "Return to" zero means the scale has to be holding something first. Without this,
  // clicking LOAD TRUCK while the panel happens to read 0 closes the load instantly.
  // It also must not close while materials are still outstanding: this panel re-zeros
  // between materials, so a between-material zero used to call "Load Complete" while
  // the loader was still filling. The honest test is whether every aggregate row has
  // made weight (or the bin has carried the whole target), plus the empty reading
  // holding for a few seconds.
  const EMPTY_HOLD_MS = 5000;
  const allAggMade = aggKeys.length > 0
    && aggKeys.every((k) => (batch?.actual_weights?.[k] || 0) > 0 && !isOpenKey?.(k));
  if (loadedAt && live != null && Math.abs(live) > BIN_EMPTY_KG) armed.current = true;
  if (!empty) emptySince.current = null;
  else if (emptySince.current == null) emptySince.current = Date.now();
  const loadedKgNow = aggKeys.reduce((s, k) => s + (batch?.actual_weights?.[k] || 0), 0);
  const doneWeighing = allAggMade || (target > 0 && loadedKgNow >= target * 0.9);
  if (loadedAt && armed.current && empty && doneWeighing
      && Date.now() - emptySince.current >= EMPTY_HOLD_MS) dischargedLatch.current = true;
  const discharged = dischargedLatch.current;

  const step = complete || discharged ? 5 : loadedAt ? 4 : reached ? 3
    : aggKeys.some((k) => (batch?.actual_weights?.[k] || 0) > 0) ? 2 : 1;

  const loadedKg = aggKeys.reduce((s, k) => s + (batch?.actual_weights?.[k] || 0), 0);
  return { live, loadedKg, target, reached, discharged, loadedAt, complete, step, empty };
}


// Resolve how much of an additive is actually stocked. The dosing CHANNEL is keyed by
// category `additive_<key>`, but operators often stock their real products under generic
// `admixture` items with their own SKUs/names (and in litres, not millilitres). So we
// take the canonical channel item AND any admixture item whose SKU/name matches, then
// show whichever holds the most — normalised to litres — so real stock isn't shown empty.

export function batchMaterials(batch, mix) {
  if (!batch) return [];
  const tw = batch.target_weights || {};
  // Friendly names for exposed rocks come from the mix design (e.g. "Basalt 10mm")
  // — the batch key only carries the SKU, so map it back for display.
  const labels = {};
  for (const r of (mix?.exposed_rocks || [])) {
    if (r?.name) labels[`exposed:${r.sku || r.name}`] = r.name;
  }
  // Dynamic exposed-aggregate rows the mix carries ("exposed:{sku|name}").
  // Operator's batching order: decorative stone (Ice Rock) goes in BEFORE the 7 mm,
  // and any exposed DUST goes in straight after it — both still ahead of sand.
  const exposedAll = Object.keys(tw).filter((k) => (tw[k] ?? 0) > 0 && k.startsWith("exposed:"));
  const isDust = (k) => /dust/i.test(labels[k] || k);
  const exposedStone = exposedAll.filter((k) => !isDust(k));
  const exposedDust = exposedAll.filter(isDust);
  const order = [];
  for (const k of MATERIAL_ORDER) {
    if (k === "agg_7mm") order.push(...exposedStone);
    order.push(k);
    if (k === "agg_7mm") order.push(...exposedDust);
  }
  return order.filter((k) => (tw[k] ?? 0) > 0)
    .map((k) => {
      const meta = metaFor(k);
      // Per-m³ comes straight from the live mix design (the recipe), not back-calculated
      // from the batched target — reuse/water-trim/older recipes would otherwise skew it.
      let perM3 = null;
      if (k.startsWith("exposed:")) {
        const sku = k.slice("exposed:".length);
        const rock = (mix?.exposed_rocks || []).find((r) => (r.sku || r.name) === sku);
        perM3 = rock ? Number(rock.kg_per_m3 || 0) : null;
      } else {
        const field = ADDITIVE_KEYS.includes(k) ? `${k}_ml` : `${k}_kg`;
        perM3 = mix && mix[field] != null ? Number(mix[field]) : null;
      }
      return { key: k, meta: labels[k] ? { ...meta, label: labels[k] } : meta, target: tw[k], actual: batch.actual_weights?.[k] ?? 0, perM3 };
    });
}

/* ── HIGH-VISIBILITY MIX BAR ─────────────────────────────────────────────── */
export function MixBar({ batch, mix, mixes, lane, onQtySaved, onMixChange, scaleFeed, liveStatus }) {
  const materials = batchMaterials(batch, mix);
  // W/C ratio — design figure (mix water ÷ mix cement). Limit is the engineer's
  // per-mix max_wc_ratio; falls back to the AS 1379 default (water + 20) / cement.
  const wcCement = Number(mix?.cement_kg || 0);
  const wcWater = Number(mix?.water_kg || 0);
  const wcRatio = wcCement > 0 ? wcWater / wcCement : null;
  const wcLimit = Number(mix?.max_wc_ratio) || (wcCement > 0 ? (wcWater + 20) / wcCement : null);
  const wcOver = wcRatio != null && wcLimit != null && wcRatio > wcLimit + 1e-9;
  const done = materials.filter((m) => m.actual > 0).length;
  const seq = useAggSequence(batch, scaleFeed, batch?.tolerance_pct ?? 1);
  const stage = seq.step;
  const loadedAt = seq.loadedAt;
  const loadComplete = seq.complete;
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(batch?.quantity_m3 ?? "");
  useEffect(() => { setVal(batch?.quantity_m3 ?? ""); }, [batch?.quantity_m3]);
  const commit = async () => {
    const q = parseFloat(val);
    setEditing(false);
    if (!batch || isNaN(q) || q <= 0 || q === batch.quantity_m3) { setVal(batch?.quantity_m3 ?? ""); return; }
    try { await api.patch(`/batches/${batch.id}`, { quantity_m3: q }); toast.success(`Load size · ${q} m³`); onQtySaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); setVal(batch.quantity_m3); }
  };
  const [reuse, setReuse] = useState(batch?.reuse_m3 ?? "");
  const [trim, setTrim] = useState(batch?.water_trim_l ?? "");
  useEffect(() => { setReuse(batch?.reuse_m3 ? String(batch.reuse_m3) : ""); setTrim(batch?.water_trim_l ? String(batch.water_trim_l) : ""); }, [batch?.id, batch?.reuse_m3, batch?.water_trim_l]);
  const saveField = async (field, raw, cur) => {
    const num = parseFloat(raw);
    const safe = isNaN(num) ? 0 : num;
    if (!batch || safe === Number(cur || 0)) return;
    try {
      await api.patch(`/batches/${batch.id}`, { [field]: safe });
      toast.success(field === "reuse_m3" ? `Reuse · ${safe} m³` : `Water trim · ${safe >= 0 ? "+" : ""}${safe} L`);
      onQtySaved?.(); window.dispatchEvent(new Event("batcher:refresh"));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const progress = materials.length ? Math.round((done / materials.length) * 100) : 0;
  const aggProgress = seq.target > 0 ? Math.min(100, Math.max(0, (Math.max(seq.loadedKg, seq.live ?? 0) / seq.target) * 100)) : progress;
  return (
    <div className="grid grid-cols-[minmax(0,2.2fr)_minmax(0,1.1fr)_minmax(0,1.6fr)_minmax(0,1.3fr)_minmax(0,1.2fr)] bg-zinc-950 border border-zinc-800 divide-x divide-zinc-800 shrink-0" data-testid="cockpit-mix-bar">
      <div className="px-4 py-2 min-w-0">
        <div className="mono text-[9px] uppercase tracking-[0.3em] text-primary-orange">Current Mix · {lane}</div>
        {batch ? (
          <div className="min-w-0">
            <div className="flex items-baseline gap-3 min-w-0">
              <div className="heading text-4xl font-black !text-white leading-none tracking-tight truncate" data-testid="cockpit-mix-code">{mix?.code || batch.mix_code}</div>
              <div className="mono text-[11px] text-zinc-300 truncate">{mix?.name || ""}{mix?.strength_mpa ? ` · ${mix.strength_mpa} MPa` : ""}{mix?.slump_mm ? ` · ${mix.slump_mm} mm` : ""}</div>
            </div>
            {wcRatio != null && (
              <div className="mono text-[10px] mt-1 flex items-center gap-1.5" data-testid="cockpit-wc-ratio">
                <span className="uppercase tracking-[0.3em] text-zinc-500">W/C</span>
                <span
                  data-testid="cockpit-wc-value"
                  className={`tabular-nums leading-none ${wcOver ? "text-red-500 font-black text-base" : "text-sky-300 font-bold text-sm"}`}>
                  {wcRatio.toFixed(2)}
                </span>
                {wcLimit != null && <span className="text-zinc-600">/ max {wcLimit.toFixed(2)}</span>}
                {wcOver && <span className="text-red-500 font-black uppercase tracking-widest text-[9px]" data-testid="cockpit-wc-alert">Out of spec</span>}
              </div>
            )}
            {onMixChange && (mixes?.length > 0) && (
              <select
                data-testid="cockpit-mix-select"
                value={batch.mix_id || ""}
                disabled={done > 0}
                title={done > 0 ? "Clear the load before changing the mix — weighing has started" : "Change the mix design for this load"}
                onChange={(e) => e.target.value && e.target.value !== batch.mix_id && onMixChange(e.target.value)}
                className="mono text-[10px] bg-black border border-zinc-700 text-zinc-200 px-1.5 py-1 mt-1 uppercase tracking-widest max-w-[240px] disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-orange">
                {[...mixes].sort((a, b2) => (a.code || "").localeCompare(b2.code || "")).map((m) => (
                  <option key={m.id} value={m.id}>{m.code}{m.name ? ` — ${m.name}` : ""}</option>
                ))}
              </select>
            )}
          </div>
        ) : <div className="heading text-3xl font-black !text-zinc-600 leading-none" data-testid="cockpit-mix-code">NO ACTIVE LOAD</div>}
      </div>
      <div className="px-4 py-2">
        <div className="mono text-[9px] uppercase tracking-[0.3em] text-primary-orange">Load Size</div>
        {editing && batch ? (
          <input autoFocus type="number" step="0.1" min="0.1" value={val} onChange={(e) => setVal(e.target.value)} onBlur={commit}
            onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setVal(batch.quantity_m3); setEditing(false); } }}
            data-testid="cockpit-qty-input" className="mono text-3xl font-black w-28 bg-black border border-primary-orange text-white px-1 leading-none" />
        ) : (
          <button type="button" disabled={!batch} onClick={() => setEditing(true)} data-testid="cockpit-qty-edit" title="Click to change load size"
            style={{ color: batch ? "rgb(var(--primary))" : "#52525B" }} className="heading text-4xl font-black leading-none tabular-nums flex items-baseline gap-1">
            {batch ? Number(batch.quantity_m3).toFixed(1) : "—"}<span className="text-base text-zinc-400">m³</span>{batch && <Pencil className="w-3 h-3 text-zinc-500 self-center" />}
          </button>
        )}
        {batch && (
          <div className="flex items-center gap-3 mt-1.5">
            <label className="flex items-center gap-1" title="Reused / returned concrete already in the drum — plant batches less fresh">
              <span className="mono text-[8px] uppercase tracking-widest text-zinc-500">Reuse</span>
              <input type="number" step="0.1" min="0" value={reuse} onChange={(e) => setReuse(e.target.value)} onBlur={() => saveField("reuse_m3", reuse, batch.reuse_m3)}
                onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} data-testid="cockpit-reuse-input"
                className="mono text-xs font-bold w-12 bg-black border border-zinc-700 focus:border-primary-orange text-white px-1 py-0.5 leading-none" />
              <span className="mono text-[8px] text-zinc-500">m³</span>
            </label>
            <label className="flex items-center gap-1" title="Manual water trim for this load (± litres)">
              <span className="mono text-[8px] uppercase tracking-widest text-sky-400">Water trim</span>
              <input type="number" step="1" value={trim} onChange={(e) => setTrim(e.target.value)} onBlur={() => saveField("water_trim_l", trim, batch.water_trim_l)}
                onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} data-testid="cockpit-water-trim-input"
                className="mono text-xs font-bold w-12 bg-black border border-zinc-700 focus:border-sky-400 text-white px-1 py-0.5 leading-none" />
              <span className="mono text-[8px] text-zinc-500">L</span>
            </label>
          </div>
        )}
      </div>
      <div className="px-4 py-2 min-w-0">
        <div className="mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">Truck · Customer</div>
        <div className="flex items-baseline gap-2 min-w-0">
          <div className="heading text-3xl font-black !text-white leading-none" data-testid="cockpit-truck">{batch?.truck_number || "—"}</div>
          <div className="mono text-[11px] text-zinc-300 truncate">{batch?.customer_name || (batch ? `Batch ${batch.id.slice(0, 8)}` : "assign a truck from the queue")}</div>
        </div>
        {batch && <div className="mono text-[10px] text-zinc-500 truncate">{batch.driver_name ? `${batch.driver_name} · ` : ""}{batch.site_name || batch.site_address || ""}</div>}
      </div>
      <div className="px-4 py-2">
        <div className="mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">Stage</div>
        {batch ? (
          <>
            <div className={`heading text-lg font-black leading-none whitespace-nowrap overflow-hidden text-ellipsis ${stage === 5 ? "!text-emerald-400" : stage === 3 ? "!text-emerald-300 animate-pulse" : "!text-white"}`} data-testid="cockpit-stage">{stage} · {STAGES[stage - 1].label}</div>
            <div className="h-1.5 bg-zinc-800 mt-1.5 relative"><div className={`absolute inset-y-0 left-0 transition-[width] duration-500 ${stage >= 3 ? "bg-emerald-500" : "bg-primary-orange"}`} style={{ width: `${aggProgress}%` }} /></div>
            <div className="mono text-[10px] text-zinc-400 mt-0.5">
              {seq.target > 0 ? <>agg bin {Math.round(Math.max(seq.loadedKg, seq.live ?? 0)).toLocaleString()} / {Math.round(seq.target).toLocaleString()} kg</> : <>{done}/{materials.length} materials · {progress}%</>}
            </div>
          </>
        ) : <div className="heading text-2xl font-black !text-zinc-600 leading-none">IDLE</div>}
      </div>
      <div className="px-4 py-2">
        <div className="mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">Status</div>
        <div className="min-h-[30px] flex items-center">
        {batch && loadComplete ? (
          <div className="heading text-2xl font-black !text-emerald-400 leading-none" data-testid="cockpit-load-complete">✓ LOAD COMPLETE</div>
        ) : batch && seq.reached && !loadedAt ? (
          <div className="heading text-2xl font-black !text-emerald-400 leading-none animate-pulse" data-testid="cockpit-load-truck-flag">▶ LOAD TRUCK</div>
        ) : batch && loadedAt ? (
          <div className="heading text-2xl font-black !text-primary-orange leading-none" data-testid="cockpit-loading-truck">DISCHARGING</div>
        ) : (
          <div style={{ color: batch?.status === "batching" ? "rgb(var(--primary))" : "#D4D4D8" }} className="heading text-2xl font-black leading-none" data-testid="cockpit-status">{batch ? batch.status.toUpperCase() : "READY"}</div>
        )}
        </div>
        <div className="mono text-[9px] uppercase tracking-widest mt-1 flex gap-2">
          <span className={liveStatus === "live" ? "text-primary-orange" : "text-red-400"}>● {liveStatus === "live" ? "LIVE" : liveStatus.toUpperCase()}</span>
          <span className={scaleFeed.state === "streaming" ? "text-emerald-400" : scaleFeed.state === "idle" ? "text-amber-400" : "text-red-400"}>▲ RINSTRUM {scaleFeed.state === "streaming" ? "LIVE" : scaleFeed.state === "idle" ? "IDLE" : "OFFLINE"}</span>
        </div>
      </div>
    </div>
  );
}

/* ── LEFT COLUMN · PLANT GRAPHICS + GAUGES ───────────────────────────────── */
export function PlantColumn({ plantId, batches, byLane }) {
  // The plant photo was replaced by the control-room board: silos left, loading bay
  // centre, aggregate bins right, with manual level entry. See PlantLiveBoard.
  return <PlantLiveBoard plantId={plantId} batches={batches} byLane={byLane} />;
}

/* ── RIGHT TOP · TRUCK QUEUE ─────────────────────────────────────────────── */
export function TruckQueue({ batches, byLane, mixes, selectedLane, onSelectLane, onAssign }) {
  const rows = batches.filter((b) => b.status === "batching" || b.status === "queued")
    .sort((a, b) => (a.status !== b.status ? (a.status === "batching" ? -1 : 1) : new Date(a.created_at || 0) - new Date(b.created_at || 0)));
  return (
    <div className="flex flex-col min-h-0 min-w-0 flex-1 bg-zinc-950 border border-zinc-800 overflow-hidden" data-testid="cockpit-truck-queue">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-zinc-800">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-primary-orange font-black flex items-center gap-2"><Truck className="w-3.5 h-3.5" /> Truck Queue</div>
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-400">In queue <span className="text-white font-black text-sm" data-testid="cockpit-queue-count">{rows.length}</span></div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        <table className="w-full mono text-[11px]">
          <thead className="sticky top-0 bg-zinc-950"><tr className="text-[8px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
            <th className="text-left px-2 py-1">#</th><th className="text-left px-2 py-1">Truck</th><th className="text-left px-2 py-1">Customer</th><th className="text-left px-2 py-1">Mix</th><th className="text-right px-2 py-1">m³</th><th className="text-left px-2 py-1">Lane</th><th className="text-right px-2 py-1">Action</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-zinc-500 uppercase tracking-widest text-[10px]">No trucks queued — batches appear here when dispatch starts a sequence</td></tr>}
            {rows.map((b, i) => {
              const mix = mixes.find((m) => m.id === b.mix_id);
              const onLane = !!b.lane_id;
              const sel = onLane && b.lane_id === selectedLane;
              return (
                <tr key={b.id} className={`border-b border-zinc-800/60 ${sel ? "bg-primary-orange/15" : b.status === "batching" ? "bg-zinc-900/60" : ""}`} data-testid={`cockpit-queue-row-${b.id}`}>
                  <td className="px-2 py-1 text-zinc-500">{i + 1}</td>
                  <td className="px-2 py-1 text-white font-black text-sm">{b.truck_number || "—"}</td>
                  <td className="px-2 py-1 text-zinc-300 truncate max-w-[160px]">{b.customer_name || b.id.slice(0, 8)}</td>
                  <td className="px-2 py-1 text-primary-orange font-black">{mix?.code || b.mix_code}</td>
                  <td className="px-2 py-1 text-right text-white font-black tabular-nums">{Number(b.quantity_m3 || 0).toFixed(1)}</td>
                  <td className="px-2 py-1">
                    {onLane ? <span className={`px-1.5 py-0.5 border text-[9px] font-black uppercase ${b.status === "batching" ? "border-primary-orange text-primary-orange" : "border-zinc-600 text-zinc-300"}`}>{b.lane_id} · {b.status}</span>
                      : <span className="px-1.5 py-0.5 border border-emerald-500/60 text-emerald-400 text-[9px] font-black uppercase">Waiting</span>}
                  </td>
                  <td className="px-2 py-1 text-right whitespace-nowrap">
                    {onLane ? (
                      <button type="button" onClick={() => onSelectLane(b.lane_id)} data-testid={`cockpit-view-${b.id}`} className={`px-2 py-0.5 border text-[9px] uppercase tracking-widest ${sel ? "border-primary-orange text-primary-orange" : "border-zinc-700 text-zinc-300 hover:border-primary-orange"}`}>{sel ? "Viewing" : "View"}</button>
                    ) : LANES.map((l, li) => (
                      <button key={l} type="button" disabled={!!byLane[l]} onClick={() => onAssign(l, b.id)} data-testid={`cockpit-assign-${b.id}-${l}`} title={byLane[l] ? `${l} busy with ${byLane[l].truck_number}` : `Load onto ${l}`}
                        className="ml-1 px-2 py-0.5 border border-primary-orange text-primary-orange hover:bg-primary-orange hover:text-black disabled:opacity-30 disabled:cursor-not-allowed text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-0.5"><ArrowRight className="w-3 h-3" /> L{li + 1}</button>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── RIGHT BOTTOM · LIVE BATCH WEIGHTS ───────────────────────────────────── */
function ManualCapture({ open, onOpenChange, material, target, unit, initial, onSave }) {
  const [val, setVal] = useState("");
  // An uncaptured row has actual 0, which is not nullish — pre-fill the target so one
  // stray click cannot silently capture zero kilograms.
  useEffect(() => { if (open) setVal((initial > 0 ? initial : target ?? 0).toString()); }, [open, initial, target]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-surface border-industrial max-w-md">
        <DialogHeader><DialogTitle className="heading uppercase">Capture {material?.label}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs mono text-muted-industrial">Target: <span className="text-primary-orange">{target?.toFixed(1)} {unit}</span></div>
          <Input data-testid="capture-value" type="number" step="0.1" autoFocus value={val} onChange={(e) => setVal(e.target.value)} className="rounded-none bg-transparent border-industrial mono text-2xl h-14" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none border-industrial uppercase text-xs">Cancel</Button>
          <Button data-testid="capture-save-btn" onClick={() => { onSave(parseFloat(val || 0)); onOpenChange(false); }} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">Capture</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LiveWeights({ lane, batch, mix, byLane, onSelectLane, scaleFeed, onCapture, onSiloChange, onLoadTruck, onComplete, onAbort, onAction, onRefresh, loader = false, onNextTruck, nextTruck, onGps }) {
  const materials = batchMaterials(batch, mix);
  // Loader (iPad) variant: glove-sized controls, no plant micro-buttons, no lane tabs.
  const BIG = loader ? "!text-sm !px-5 !py-4 min-h-[64px] !tracking-wider" : "";
  const [pushing, setPushing] = useState(false);
  const [sentLive, setSentLive] = useState(null);   // batch id with a verified live write (server-checked)
  const [starting, setStarting] = useState(false);
  const [started, setStarted] = useState(null);
  const [lastWrite, setLastWrite] = useState(null);
  /* ── OPEN ROWS (the "extra of the same material" freeze) ────────────────────
     When this code auto-locks a row BELOW target, the loader is usually going back
     to the same bin for another pass (always the 14 mm here). The row used to be
     locked and the sequence counted it as done, so the screen sat frozen. Now the
     row stays OPEN: it keeps showing live weight, keeps climbing, and only locks
     when it makes weight (or the operator accepts it by hand). */
  const openRef = useRef({});
  const isOpenKey = useCallback((k) => !!openRef.current[k], []);
  const isLockedRow = (m) => m.actual > 0 && !openRef.current[m.key];
  useEffect(() => {
    if (!batch?.id) { setSentLive(null); setStarted(null); setLastWrite(null); return; }
    let alive = true;
    const check = () => api.get(`/scale/start/eligible?batch_id=${batch.id}`)
      .then(({ data }) => { if (!alive) return; setSentLive(data.eligible ? batch.id : null); setStarted(data.started ? batch.id : null); setLastWrite(data.last_write || null); })
      .catch(() => {});
    check();
    const t = setInterval(check, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [batch?.id]);
  const pressStart = async () => {
    if (!batch?.id || !window.confirm("Press START on the Rinstrum? The loader's on-screen sequence will begin.")) return;
    setStarting(true);
    try {
      await api.post("/scale/start", { batch_id: batch.id, plant_id: batch.plant_id || "P01" });
      setStarted(batch.id);
      toast.success("START sent to the panel");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Could not send START");
    } finally { setStarting(false); }
  };
  const sendTargets = async () => {
    setPushing(true);
    try {
      const targets = Object.fromEntries(materials.map((m) => [m.key, m.target]));
      const { data } = await api.post("/scale/targets/push", { batch_id: batch?.id, plant_id: batch?.plant_id || "P01", targets, product: mix?.k412_product || undefined, load_m3: batch?.quantity_m3 });
      const recipe = data.product ? `recipe ${data.product}` : "the recipe the panel is on";
      if (data.mode === "live") setSentLive(batch?.id);
      toast.success(data.mode === "live"
        ? `Sending ${data.lines.length} targets to the scale · ${recipe} · ${data.proportion_m3} m³`
        : `Dry run queued · ${data.lines.length} targets · ${recipe} (nothing written)`);
      if (!data.product) toast.warning("This mix has no K412 recipe set — targets go into whatever recipe the panel shows. Set it in Mix Designs.");
      if (data.conflicts?.length) toast.warning(data.conflicts.join(" · "));
      if (data.skipped?.length) toast.warning(`Not sent to the K412: ${data.skipped.join(", ")} (no material number in Settings → Scale Writing)`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Could not queue targets");
    } finally {
      setPushing(false);
    }
  };
  const tolerance = batch?.tolerance_pct ?? 1;
  const seq = useAggSequence(batch, scaleFeed, tolerance, isOpenKey);
  const stage = seq.step;
  const loadedAt = seq.loadedAt;
  const loadComplete = seq.complete;
  const [manual, setManual] = useState(null);
  // GADTECH runs SEPARATE weighers (confirmed by the operator), so a reading belongs to
  // whichever material the panel names — never subtract what is already locked.
  // Rinstrum noise makes the last digit dance even when the load is settled, so snap the
  // live reading to 2 kg steps to keep the number still on screen.
  const snap = (w) => Math.round(w / 2) * 2;
  /* ── UNNAMED AGG READINGS ──────────────────────────────────────────────────
     Standard bins come down the bridge with a material key, but custom/exposed
     products (ICE ROCK, WHITE DUST…) often arrive with a name this app can't match,
     or with no name at all. Those readings used to be discarded as "bin total", so
     those rows never counted and never reached the docket. Now an unnamed agg
     reading is attributed to the row the plant is UP TO in batching order, and if the
     panel is reporting a running bin total we take off what's already locked so four
     rows can't all read the same number. */
  const aggRows = materials.filter((m) => scaleForKey(m.key) === "agg" && m.target > 0);
  const currentAggKey = (aggRows.find((m) => !isLockedRow(m)) || {}).key || null;
  const lockedAggSum = aggRows.filter(isLockedRow).reduce((s, m) => s + (m.actual || 0), 0);
  // Shared cumulative agg hopper → a row's portion is the running total minus the
  // aggregates locked BEFORE it. Crucially we exclude the row's OWN locked weight: if we
  // subtracted that too, a locked row still being topped up by the loader would compute
  // its portion as ~0 (live gauge reads 0), self-correction could never raise it, and the
  // surplus would spill onto the next aggregate row ("throws the weight into the next agg").
  // Subtract as soon as the reading sits above that prior sum (no dead-zone). A re-zeroed
  // per-material weigher reads its own small weight (below the sum), so this is a no-op there.
  const priorLockedFor = (key) => (key
    ? aggRows.filter((m) => isLockedRow(m) && m.key !== key).reduce((s, m) => s + (m.actual || 0), 0)
    : lockedAggSum);
  // Per-row weigher MODE. This plant re-zeros between aggregates and counts up fresh for
  // each material (operator-confirmed), but on some passes it streams a running bin
  // total. A "fresh" row reads the material's OWN weight, so adjAgg must NOT take the
  // earlier bins off it — subtracting collapsed a big bin the moment its own reading
  // passed the prior-locked sum (e.g. man dust 3000 vs 7 mm locked 2480), freezing the
  // row low on fast pours until the operator pressed ACCEPT. Detection lives in the
  // override-killer effect below; here we just honour it.
  const adjAgg = (raw, key) => {
    if (key && aggModeRef.current[key] === "fresh") return raw;
    const prior = priorLockedFor(key);
    return (prior > BIN_EMPTY_KG && raw > prior ? raw - prior : raw);
  };
  const liveFor = (key) => {
    const sid = scaleForKey(key);
    if (!sid || !scaleFeed.online) return null;
    const r = scaleFeed.scaleById?.[sid];
    if (!r || r.weight_kg == null) return null;
    // A stale reading is a frozen number, not a live weight — never offer it for
    // capture and never let it decide whether the weighers are empty.
    if (r.stale) return null;
    // DUMP/TARE/ZERO records are the panel discharging or being reset — that weight
    // is NOT a material being weighed. Painting it into the rows made every
    // aggregate row read the same number and lag a material behind.
    if (isActionReading(r)) return null;
    const rMat = materialKeyOf(r, materials);
    let raw = r.weight_kg;
    if (!rMat) {
      if (sid === "agg" && (!currentAggKey || key !== currentAggKey)) return null;
    } else if (rMat !== key) return null;
    // Shared cumulative aggregate weigher: every aggregate tips into ONE hopper, so the
    // reading is a RUNNING TOTAL even when the panel names the material. Subtract what
    // earlier aggregates already locked — adjAgg is a no-op unless the reading exceeds
    // that sum, so a re-zeroed per-material weigher is left untouched. Stops the 14 mm
    // weight carrying onto the 10 mm row and pinning it red.
    if (sid === "agg") raw = adjAgg(raw, key);
    const w = snap(raw);
    // An empty or un-tared panel reads 0 (some Rinstrum panels send -100). That is
    // not a weigh-in-progress, so don't stream it or show a bogus -100% delta.
    if (w <= 0) return null;
    return { ...r, weight_kg: w };
  };

  // Keep the last live weight for each un-locked material on screen even after the
  // panel's reading moves on, so a number can't vanish before the operator accepts
  // it (the "jumps to the next line" problem). Reset when the batch changes.
  const heldRef = useRef({});
  const autoAcceptedRef = useRef({});
  const correctedRef = useRef({});
  /* TOP-UP MATHS. This panel sometimes re-zeros for a second pass on the same bin.
     When we reopen a line we look at the first reading: well BELOW what's already in
     the bin means the panel started again, so the pass is ADDITIVE; at or above means
     it kept counting, so the reading is already the running total. */
  const topupRef = useRef({});
  const aggModeRef = useRef({});   // per agg row: "fresh" (re-zeroed own weight) | "cumulative" (running total)
  const eff = (key, raw) => {
    if (raw == null) return null;
    const t = topupRef.current[key];
    return t && t.mode === "add" ? t.base + raw : raw;
  };
  useEffect(() => { heldRef.current = {}; autoAcceptedRef.current = {}; correctedRef.current = {}; openRef.current = {}; topupRef.current = {}; aggModeRef.current = {}; }, [batch?.id]);

  // Auto-accept a material the instant it MAKES WEIGHT (inside tolerance of target).
  // It deliberately does NOT wait for the panel's stable/motion flag: with a loader
  // running, this Rinstrum reports `M` (motion) on every single record and then resets
  // straight to the next material — so waiting for `S` meant the row never locked and
  // the number "held then dropped back instead of accepting when it made weight".
  useEffect(() => {
    if (!scaleFeed.online || !batch?.id) return;
    for (const m of materials) {
      const open = openRef.current[m.key];
      if (!m.target || ((m.actual > 0 || autoAcceptedRef.current[m.key]) && !open)) continue;
      const w = eff(m.key, liveFor(m.key)?.weight_kg ?? heldRef.current[m.key]);
      if (w == null) continue;
      if (Math.abs(dev(w, m.target)) <= tolerance) {
        autoAcceptedRef.current[m.key] = true;
        delete openRef.current[m.key];       // made weight → the row is final
        onCapture(m.key, Number(w.toFixed(1)));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scaleFeed, tolerance, batch?.id]);

  /* ── THE OVERRIDE KILLER ──────────────────────────────────────────────────────
     The panel runs its OWN presets, which don't match the mix design: on a 40 MPA
     TANK MIX the app wanted 2952 kg of 14 mm and the loader delivered 2840 (-3.8%),
     2232 of dust and got 2020 (-9.5%). Normal loader accuracy, but outside ±3%, so
     the "made weight" rule above never fired and the batcher had to override every
     row before the driver reached the next bin.

     So a row also locks with NO reference to the app's target. Two signals:
       · the panel starts naming a DIFFERENT material — debounced over two readings so
         one stray record can't end a material early
       · the weight has STOPPED GROWING for SETTLE_MS and is already most of the
         target — the loader has finished with that bin

     DO NOT reintroduce a "value dropped back = finished" rule. It looks right and it
     is wrong: this panel re-zeros PART WAY THROUGH a material and counts up again
     (observed on sand: 2050 → 350 → 1430 → 2790 → 3230), so a drop locked sand at
     2050 against a 2800 target while the loader was still filling it.
  ─────────────────────────────────────────────────────────────────────────────── */
  const SETTLE_MS = 5000;        // used only by self-correction (raise an auto-locked row)
  const nameRef = useRef({ current: null, candidate: null, seen: 0 });
  const settleRef = useRef({});
  const finalRef = useRef({});        // rows the OPERATOR closed — never auto-reopen
  const aggViewRef = useRef({ named: null, bin: null });
  useEffect(() => {
    nameRef.current = { current: null, candidate: null, seen: 0 };
    settleRef.current = {};
    finalRef.current = {};
    aggViewRef.current = { named: null, bin: null };
  }, [batch?.id]);
  useEffect(() => {
    if (!scaleFeed.online || !batch?.id) return;
    const lock = (key, floor) => {
      const m = materials.find((x) => x.key === key);
      if (!m || (m.actual > 0 && !openRef.current[key]) || (autoAcceptedRef.current[key] && !openRef.current[key]) || !m.target) return;
      const w = eff(key, heldRef.current[key]);
      if (w == null || w < m.target * floor) return;
      if (openRef.current[key] && w <= (m.actual || 0)) return;   // mid top-up, nothing new yet
      autoAcceptedRef.current[key] = true;
      // Under target by more than tolerance → the loader is coming back for another
      // pass on this bin. Keep the row OPEN instead of freezing the sequence.
      if (dev(w, m.target) < -tolerance) openRef.current[key] = true;
      else delete openRef.current[key];
      onCapture(key, Number(w.toFixed(1)));
    };
    const r = scaleFeed.scaleById?.agg;
    if (!r || r.stale || isActionReading(r) || r.weight_kg == null) return;
    let key = materialKeyOf(r, materials);
    let rawW = r.weight_kg;
    if (!key) {
      // Unnamed agg reading — it belongs to the row the plant is up to.
      if (!currentAggKey) return;
      key = currentAggKey;
    }
    // Decide whether this weigher RE-ZEROED for the current material (reads its own
    // weight) or is streaming a RUNNING TOTAL. A reading sitting clearly below the
    // earlier-locked aggregates, OR one that has dropped back below this row's own peak,
    // can only be a re-zero → mark the row "fresh" and never subtract the prior bins off
    // it (the freeze fix). Otherwise, the first reading at/above the prior sum is a
    // genuine running total → "cumulative", keep subtracting (stops 14 mm carrying onto
    // 10 mm). A later re-zero flips it back to fresh.
    const priorL = priorLockedFor(key);
    const peakSeen = heldRef.current[key] ?? 0;
    if ((priorL > BIN_EMPTY_KG && rawW < priorL - 100) || (peakSeen > BIN_EMPTY_KG && rawW < peakSeen - 100)) {
      aggModeRef.current[key] = "fresh";
    } else if (aggModeRef.current[key] == null && priorL > BIN_EMPTY_KG && rawW >= priorL) {
      aggModeRef.current[key] = "cumulative";
    }
    // Shared cumulative aggregate weigher reads a RUNNING TOTAL even when the panel
    // names the material, so always drop the already-locked aggregates. adjAgg only
    // subtracts when the reading exceeds that sum (a no-op on a re-zeroed per-material
    // weigher) — this keeps the 14 mm weight from carrying onto the 10 mm row.
    rawW = adjAgg(rawW, key);
    if (rawW <= 0) return;
    // Record the running peak for this row: the settle-lock and the override-killer
    // both read it, and it used to be written only during a top-up pass — so a row
    // that stopped growing never locked itself.
    heldRef.current[key] = Math.max(heldRef.current[key] ?? 0, rawW);

    /* 2b · CHECKPOINT CAP (shared cumulative agg bin only). Readings on the agg bin
       are a RUNNING TOTAL with no material name, so once the CURRENT material's delta
       has clearly overshot its target the loader has already started the NEXT
       aggregate — the extra weight is NOT this material. Lock it at its target and
       let the surplus roll onto the next row (adjAgg subtracts the new locked sum on
       the following reading). Without this, continuous loading that skips the narrow
       ±tolerance auto-accept window let one row balloon (e.g. 10 mm reading 4770 kg
       against a 1260 kg target) until a settle pause finally caught it.
       Guarded: unnamed agg totals only, generous overshoot so normal loader variance
       still locks at its true weight via auto-accept / settle, never on operator-closed
       rows. */
    if (!loadedAt && !finalRef.current[key]) {
      const cm = materials.find((x) => x.key === key);
      const alreadyDone = autoAcceptedRef.current[key] && !openRef.current[key];
      if (cm && cm.target > 0 && !alreadyDone && rawW > cm.target * 1.2 + 150) {
        autoAcceptedRef.current[key] = true;
        delete openRef.current[key];
        onCapture(key, Number(cm.target.toFixed(1)));   // cap at target; surplus → next row
        toast.info(`${cm.meta.label} reached target — advancing (surplus rolled to next bin)`);
        return;
      }
    }

    /* 0 · TOP-UP WATCH. Any bin that already has weight in it but is still short of
       target is fair game for another pass: wake the line up (if we'd closed it) and
       work out whether the panel is still counting the running total or has TARED and
       started again — because on a tare the new reading has to be ADDED to what is
       already in the bin or the docket would under-count the load. Re-checked on every
       reading, so it's right whether the tare happens before or during the pass.
       Rows the operator closed by hand are final and never reopen. */
    if (!loadedAt && !finalRef.current[key]) {
      const back = materials.find((x) => x.key === key);
      const have = back?.actual || 0;
      if (back && back.target > 0 && have > 0 && dev(have, back.target) < -tolerance) {
        if (!openRef.current[key]) {
          openRef.current[key] = true;
          heldRef.current[key] = rawW;
          settleRef.current[key] = { peak: rawW, at: Date.now() };
          toast.info(`${back.meta.label} back on the weigher — topping up`);
        }
        const t = topupRef.current[key];
        if (rawW < have * 0.6) {
          // Panel has tared — this pass counts on top of what's in the bin.
          if (!t || t.mode !== "add" || t.base !== have) {
            topupRef.current[key] = { base: have, mode: "add" };
            heldRef.current[key] = rawW;       // old raw peak belongs to the last pass
            settleRef.current[key] = { peak: rawW, at: Date.now() };
          }
        } else if (rawW >= have * 0.9 && t) {
          delete topupRef.current[key];               // panel is back on the running total
        }
      }
    }

    // 1 · the panel has moved to another material → the previous one is done
    const n = nameRef.current;
    if (key === n.current) { n.candidate = null; n.seen = 0; }
    else if (key === n.candidate) {
      if (++n.seen >= 2) {
        const prev = n.current;
        nameRef.current = { current: key, candidate: null, seen: 0 };
        // Only close the previous bin on a name change if it has ALREADY MADE WEIGHT.
        // On this loader-fed plant a bin must stay LIVE until it hits its target, so a
        // stray/early name blip must never close a half-full bin (that was the "not
        // staying alive long enough" problem).
        if (prev) lock(prev, Math.max(0, (100 - tolerance) / 100));
      }
    } else { n.candidate = key; n.seen = 1; }

    // 2 · track the running peak (self-correction below reads it). We DO NOT close a bin
    //     just because the weight paused — on this loader-fed cumulative hopper the time
    //     between aggregates / dust / sand varies, so a pause is NOT a finished bin. The
    //     bin stays LIVE until it reaches the required weight.
    const peak = Math.max(heldRef.current[key] ?? 0, rawW);
    const s = settleRef.current[key];
    if (!s || peak > s.peak + 1) settleRef.current[key] = { peak, at: Date.now() };

    // 3 · MADE WEIGHT → CLOSE AND MOVE ON. Within tolerance the auto-accept effect locks
    //     the bin the instant it reaches target. Here we also close it if a single bucket
    //     jumped PAST target between readings, so it still closes at its real weight and
    //     shows red (over) instead of ballooning — a clearly-next-material jump is already
    //     capped-and-rolled by the checkpoint above.
    if (!loadedAt && !finalRef.current[key]) {
      const cm = materials.find((x) => x.key === key);
      const w = eff(key, rawW);
      const done = autoAcceptedRef.current[key] && !openRef.current[key];
      if (cm && cm.target > 0 && w != null && !done && dev(w, cm.target) > tolerance) {
        autoAcceptedRef.current[key] = true;
        delete openRef.current[key];
        onCapture(key, Number(w.toFixed(1)));   // real weight → over target shows red
        return;
      }
    }

    // 3 · SELF-CORRECTION. If a row this code locked itself turns out to be low —
    // the loader kept filling after we called it done — raise it instead of making
    // the operator notice and override. Only ever applies to auto-locked rows, only
    // while still loading, and only upward: more material genuinely went in.
    if (!loadedAt && autoAcceptedRef.current[key]) {
      const m = materials.find((x) => x.key === key);
      const floor = Math.max(m?.actual || 0, correctedRef.current[key] || 0);
      const settled = s && Date.now() - s.at >= SETTLE_MS && peak <= s.peak + 1;
      const w = eff(key, rawW);
      if (m && m.actual > 0 && settled && w > floor * 1.02) {
        correctedRef.current[key] = w;
        onCapture(key, Number(w.toFixed(1)));
      }
    }
    // 4 · the row we left OPEN has now made weight on the second pass → lock it and
    // let the sequence move on. No settle wait: it is on target, that is the signal.
    if (!loadedAt && openRef.current[key]) {
      const om = materials.find((x) => x.key === key);
      const w = eff(key, rawW);
      if (om && om.target && dev(w, om.target) >= -tolerance) {
        delete openRef.current[key];
        delete topupRef.current[key];
        correctedRef.current[key] = w;
        onCapture(key, Number(w.toFixed(1)));
        tone(880, 0.2);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scaleFeed, batch?.id]);

  // Accepting locks whatever is on screen immediately — the operator judges the number.
  // But a weight outside tolerance is worth one styled question before it reaches the
  // docket (a browser confirm() is too small and easy to fat-finger on a plant touch-screen).
  const [confirmOver, setConfirmOver] = useState(null); // { m, value, off, dir }
  /* ── MIX CORRECTION AFTER AN OVER-WEIGH ──────────────────────────────────────
     The loader tips 6300 kg into a 5925 kg target: the design isn't wrong, every
     other material is now relatively short. Scale the whole design UP by that factor
     and the proportions, w/c ratio and strength stay on design — the load just yields
     a bit more. Ordered m³ on the docket is untouched (operator's call). */
  const [rebal, setRebal] = useState(null);     // server proposal
  const [rebalBusy, setRebalBusy] = useState(false);
  const overRow = materials.find((m) => isLockedRow(m) && m.target > 0 && dev(m.actual, m.target) > tolerance);
  const askRebalance = async () => {
    if (!batch?.id) return;
    setRebalBusy(true);
    try { setRebal((await api.post(`/batches/${batch.id}/rebalance`, { apply: false })).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not work out a correction"); }
    finally { setRebalBusy(false); }
  };
  const applyRebalance = async () => {
    setRebalBusy(true);
    try {
      const { data } = await api.post(`/batches/${batch.id}/rebalance`, { apply: true });
      toast.success(`Targets lifted ${((data.factor - 1) * 100).toFixed(1)}% — mix back on design`);
      setRebal(null);
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply the correction"); }
    finally { setRebalBusy(false); }
  };

  // An operator decision is FINAL — close the row off (no more auto top-up).
  const operatorCapture = (key, value) => {
    autoAcceptedRef.current[key] = true;
    finalRef.current[key] = true;       // operator's call — stop reopening this line
    delete openRef.current[key];
    onCapture(key, value);
  };
  const accept = (m, value) => {
    const off = dev(value, m.target);
    if (Math.abs(off) > tolerance) {
      setConfirmOver({ m, value, off, dir: off < 0 ? "under" : "over" });
      return;
    }
    operatorCapture(m.key, Number(value.toFixed(1)));
  };
  const confirmAcceptNow = () => {
    if (confirmOver) operatorCapture(confirmOver.m.key, Number(confirmOver.value.toFixed(1)));
    setConfirmOver(null);
  };

  // Load countdown. Only dry weights (kg) are summed — litres and millilitres of water
  // and admix don't belong in a kilogram total.
  const kgRows = materials.filter((m) => m.meta.unit === "kg");
  const totalTarget = kgRows.reduce((s, m) => s + (m.target || 0), 0);
  const totalBatched = kgRows.reduce((s, m) => s + (m.actual || 0), 0);
  const totalToGo = kgRows.reduce((s, m) => s + Math.max(0, (m.target || 0) - (m.actual || 0)), 0);
  const pendingCount = materials.filter((m) => !isLockedRow(m)).length;
  const scaleReadings = Object.values(scaleFeed.scaleById || {}).filter((r) => !r.stale);
  const onScales = scaleReadings.reduce((s, r) => s + Math.abs(r.weight_kg || 0), 0);

  // ── Cutoff prediction ────────────────────────────────────────────────────
  // Material keeps flowing for a beat after the gate shuts (in-flight + close lag),
  // so watch the fill RATE and warn ~1s before the number lands on target. Closing
  // then means the weigher settles ON the number instead of sailing past it.
  const CUTOFF_LEAD_S = 1.0;   // how far ahead of target to raise the "cut" flag
  const flowRef = useRef({});  // sid -> { t, w, rate(kg/s) }
  useEffect(() => { flowRef.current = {}; }, [batch?.id]);
  const rates = {};
  if (scaleFeed.online) {
    const now = Date.now();
    for (const sid of ["agg", "cement", "water"]) {
      const r = scaleFeed.scaleById?.[sid];
      if (!r || r.weight_kg == null || r.stale) { flowRef.current[sid] = null; continue; }
      const w = r.weight_kg, prev = flowRef.current[sid];
      if (!prev) { flowRef.current[sid] = { t: now, w, rate: 0 }; }
      else if (now - prev.t >= 120) {              // resample no faster than ~8Hz
        const inst = (w - prev.w) / ((now - prev.t) / 1000);
        const rate = prev.rate ? prev.rate * 0.5 + inst * 0.5 : inst;   // EMA smooth the noise
        flowRef.current[sid] = { t: now, w, rate };
      }
      rates[sid] = flowRef.current[sid]?.rate ?? 0;
    }
  }
  // True while a material is still filling and will hit target within the lead window.
  const cutoffFor = (m, shown, captured) => {
    if (captured || shown == null) return false;
    const rate = rates[scaleForKey(m.key)] || 0;
    if (rate <= 0) return false;                    // not filling (settled or discharging)
    const remaining = m.target - shown;
    if (remaining <= 0) return false;               // already at/over target
    return remaining / rate <= CUTOFF_LEAD_S;       // will land on target within a beat
  };

  /* ── SPOKEN COUNTDOWN (loader cab) ────────────────────────────────────────
     "Bin two, fourteen mil, target five nine two five … six hundred to go …
     two fifty … one hundred … CUT … on weight." Eyes stay on the bucket.
     Aggregate bins and sand only — the ones the loader is actually driving. */
  const callRef = useRef({ key: null, said: new Set() });
  useEffect(() => { callRef.current = { key: null, said: new Set() }; }, [batch?.id]);
  useEffect(() => {
    if (!loader || !getLoaderVoiceOn() || loadedAt) return;
    const r = scaleFeed.scaleById?.agg;
    if (!r || r.stale || isActionReading(r) || r.weight_kg == null) return;
    const m = materials.find((x) => x.key === materialKeyOf(r, materials));
    if (!m || !m.target || scaleForKey(m.key) !== "agg") return;

    const c = callRef.current;
    if (c.key !== m.key) {
      c.key = m.key;
      c.said = new Set();
      if (!isLockedRow(m)) {
        const bin = BIN_NO[m.key] ? `Bin ${BIN_NO[m.key]}, ` : "";
        speakLoader(`${bin}${m.meta.label.replace(/\bMM\b/i, "mil")}, target ${Math.round(m.target)}`);
      }
      return;
    }
    if (isLockedRow(m)) return;

    const shown = Math.max(eff(m.key, r.weight_kg) ?? 0, eff(m.key, heldRef.current[m.key] ?? 0), m.actual || 0);
    const remaining = m.target - shown;
    const steps = calloutSteps(m.target);
    // "Cut" is a near-target call. A fast-moving bucket can make the rate maths look
    // like we're about to land from a long way out, so only call it inside the first
    // rung of the ladder.
    if (remaining > 0 && remaining <= (steps[0] || m.target * 0.1) && cutoffFor(m, shown, false)) {
      if (!c.said.has("cut")) { c.said.add("cut"); speakLoader("Cut"); }
      return;
    }
    if (remaining <= 0) return;
    for (const step of steps) {
      if (remaining <= step && !c.said.has(step)) {
        c.said.add(step);
        speakLoader(`${step} to go`);
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scaleFeed, loader]);

  // ── SEQUENCE SIGNALS ─────────────────────────────────────────────────────
  // `seq` (useAggSequence, top of file) owns every transition. This block only
  // turns those transitions into sound. Both are latched once per batch.
  const aggDischarged = seq.discharged;

  // Short beep the moment a material lands inside tolerance — the batcher can keep
  // their eyes on the truck instead of the screen. Once per material per batch.
  const beeped = useRef(new Set());
  useEffect(() => { beeped.current = new Set(); }, [batch?.id]);
  useEffect(() => {
    for (const m of materials) {
      if (m.actual > 0 || beeped.current.has(m.key)) continue;
      const live = liveFor(m.key);
      if (live?.weight_kg == null || !m.target) continue;
      if (Math.abs(dev(eff(m.key, live.weight_kg), m.target)) <= tolerance) {
        beeped.current.add(m.key);
        tone(880, 0.2);
        if (loader && scaleForKey(m.key) === "agg") speakLoader("On weight");
      }
    }
  });

  // STEP 3 · "LOAD TRUCK" — the agg bin has reached the sum of its targets.
  const truckCalled = useRef(null);
  useEffect(() => {
    if (!batch?.id || truckCalled.current === batch.id) return;
    if (!seq.reached || seq.loadedAt || seq.complete) return;
    truckCalled.current = batch.id;
    speakLoadTruck(batch.truck_number);
  }, [batch?.id, batch?.truck_number, seq.reached, seq.loadedAt, seq.complete]);

  // STEP 6 · "LOAD COMPLETE" — the bin is back to zero. Audio fires the instant the
  // bin reads empty (the operator needs to hear it at the truck, not 2 s later).
  const chimedRef = useRef(null);
  useEffect(() => {
    if (!batch?.id || chimedRef.current === batch.id) return;
    if (!seq.complete && !seq.discharged) return;
    chimedRef.current = batch.id;
    speakLoadComplete(batch.truck_number);
  }, [batch?.id, batch?.truck_number, seq.complete, seq.discharged]);

  // …and close the load off immediately: docket issued, truck → SLUMPING.
  // Fire when EITHER the cockpit latched the discharge itself (seq.discharged)
  // OR the backend Auto-Monitor has already confirmed LOAD COMPLETE by stamping
  // ready_at (seq.complete). Previously `seq.complete` short-circuited this
  // effect, so whenever the server stamped ready_at before the cockpit's own
  // discharge check latched, the docket was never issued and the truck stayed
  // stuck in the queue — the intermittent "some trucks don't disappear" bug.
  const autoDone = useRef(null);
  useEffect(() => {
    if (!batch?.id || autoDone.current === batch.id) return;
    if (!seq.complete && !seq.discharged) return;
    autoDone.current = batch.id;
    onComplete?.(true);
  }, [batch?.id, seq.complete, seq.discharged, onComplete]);

  /* ── BIG NUMBERS (loader cab) ───────────────────────────────────────────────
     The table is unreadable from the seat of a loader. While a bin is actually
     filling, that one weight takes the whole screen; it drops back to the table
     the moment the line locks. Tap the number to get the list back early. */
  const nowRow = loader
    ? materials.find((m) => !isLockedRow(m) && liveFor(m.key)?.weight_kg != null && !liveFor(m.key).stale)
    : null;
  const [bigOff, setBigOff] = useState(null);     // material key the operator dismissed
  useEffect(() => { setBigOff(null); }, [batch?.id, nowRow?.key]);
  /* Stand the overlay down by itself once the gate shuts on a line that still needs a
     decision (static for 5 s and out of tolerance) — otherwise the operator has to tap
     the big number away before they can reach that row's ACCEPT. */
  const bigWaiting = (() => {
    if (!nowRow?.target) return false;
    const st = settleRef.current[nowRow.key];
    const w = eff(nowRow.key, heldRef.current[nowRow.key] ?? liveFor(nowRow.key)?.weight_kg);
    return !!st && Date.now() - st.at >= 5000 && w != null && Math.abs(dev(w, nowRow.target)) > tolerance;
  })();
  const bigRow = nowRow && bigOff !== nowRow.key && !bigWaiting ? nowRow : null;

  return (
    <div className="flex flex-col min-h-0 min-w-0 flex-1 bg-zinc-950 border border-zinc-800 overflow-hidden" data-testid="cockpit-live-weights">
      {bigRow && (() => {
        const lw = Math.max(eff(bigRow.key, liveFor(bigRow.key)?.weight_kg ?? 0), eff(bigRow.key, heldRef.current[bigRow.key] ?? 0), bigRow.actual || 0);
        const toGo = Math.max(0, bigRow.target - lw);
        const p = Math.min(120, pct(lw, bigRow.target));
        const off = dev(lw, bigRow.target);
        const inTol = Math.abs(off) <= tolerance;
        const over = off > tolerance;
        const cut = cutoffFor(bigRow, lw, false);
        return (
          <div className="loader-big fixed inset-0 z-50 flex flex-col" style={{ "--now": bigRow.meta.color }} data-testid={`loader-big-${lane}`} onClick={() => setBigOff(bigRow.key)}>
            <div className="flex items-center gap-4 px-6 pt-4 shrink-0">
              <span className="mono text-sm uppercase tracking-[0.35em] font-black opacity-70">{lane.replace("-", " ")}</span>
              <span className="heading text-3xl font-black" style={{ color: "rgb(var(--primary))" }} data-testid="loader-big-truck">{batch?.truck_number || "—"}</span>
              <span className="mono text-sm uppercase tracking-widest opacity-60 truncate">{mix?.code || batch?.mix_code} · {batch?.quantity_m3} m³</span>
              <span className="ml-auto mono text-[11px] uppercase tracking-[0.3em] opacity-50">Tap anywhere for the list</span>
            </div>
            <div className="flex items-baseline gap-5 px-6 mt-1 shrink-0">
              {BIN_NO[bigRow.key] ? <span className="mono text-2xl font-black opacity-60" data-testid="loader-big-bin">BIN {BIN_NO[bigRow.key]}</span> : null}
              <span className="heading font-black uppercase leading-none text-5xl" style={{ color: "var(--now)" }} data-testid="loader-big-material">{bigRow.meta.label}</span>
              {cut && <span className="mono text-base uppercase tracking-widest px-3 py-1 bg-amber-500 text-black font-black animate-pulse inline-flex items-center gap-2" data-testid="loader-big-cut"><Scissors className="w-5 h-5" /> close gate</span>}
            </div>
            <div className="flex-1 min-h-0 flex items-center justify-center px-6">
              <div className="heading font-black leading-[0.8] tabular-nums text-center" data-testid="loader-big-weight"
                style={{ fontSize: "min(34vh, 22vw)", color: over ? "#EF4444" : inTol ? "#22C55E" : cut ? "#FBBF24" : "currentColor" }}>
                {lw.toFixed(0)}<span className="heading font-black" style={{ fontSize: "0.22em", opacity: 0.55 }}> {bigRow.meta.unit}</span>
              </div>
            </div>
            <div className="shrink-0 px-6 pb-4">
              <div className="flex items-end justify-between tabular-nums">
                <div><div className="mono text-sm uppercase tracking-[0.3em] opacity-60">To go</div>
                  <div className="heading font-black leading-none" style={{ fontSize: "12vh", color: toGo <= 0 ? "#22C55E" : "rgb(var(--primary))" }} data-testid="loader-big-togo">{toGo.toFixed(0)}</div></div>
                <div className="text-right"><div className="mono text-sm uppercase tracking-[0.3em] opacity-60">Target</div>
                  <div className="heading font-black leading-none opacity-80" style={{ fontSize: "7vh" }} data-testid="loader-big-target">{bigRow.target.toFixed(0)}</div>
                  <div className={`mono text-xl font-black mt-1 ${inTol ? "text-emerald-400" : over ? "text-red-400" : "text-amber-400"}`} data-testid="loader-big-dev">{off >= 0 ? "+" : ""}{off.toFixed(1)}%</div></div>
              </div>
              <div className="relative h-8 mt-3 bg-black/40 border border-white/20 overflow-hidden">
                <div className="absolute inset-y-0 bg-emerald-500/20 border-x-2 border-emerald-500/70" style={{ left: `${((100 - tolerance) / 120) * 100}%`, width: `${((2 * tolerance) / 120) * 100}%` }} />
                <div className="absolute inset-y-0 left-0 transition-[width] duration-300" style={{ width: `${(p / 120) * 100}%`, background: over ? "#EF4444" : inTol ? "#22C55E" : "var(--now)" }} />
                <div className="absolute inset-y-0 border-l-4 border-white/90" style={{ left: `${(100 / 120) * 100}%` }} />
              </div>
            </div>
          </div>
        );
      })()}
      {loader ? (
        <div className="flex items-center border-b border-zinc-800 shrink-0 px-3 py-2" data-testid={`loader-lane-head-${lane}`}>
          <div className="heading text-2xl font-black uppercase !text-white tracking-wide">{lane.replace("-", " ")}</div>
          <div className={`ml-3 heading text-2xl font-black ${batch ? "" : "!text-zinc-600"}`} style={batch ? { color: "rgb(var(--primary))" } : undefined} data-testid={`loader-lane-truck-${lane}`}>{batch ? (batch.truck_number || "—") : "IDLE"}</div>
          {batch && <div className="ml-3 mono text-xs uppercase tracking-widest text-zinc-400 truncate">{mix?.code || batch.mix_code} · <span className="text-white font-black">{batch.quantity_m3} m³</span>{batch.customer_name ? ` · ${batch.customer_name}` : ""}</div>}
          <Activity className={`ml-auto w-5 h-5 ${scaleFeed.state === "streaming" ? "animate-pulse text-emerald-400" : "text-zinc-600"}`} />
        </div>
      ) : (
      <div className="flex items-center border-b border-zinc-800 shrink-0">
        {LANES.map((l) => {
          const lb = byLane[l];
          return (
            <button key={l} type="button" onClick={() => onSelectLane(l)} data-testid={`cockpit-lane-tab-${l}`}
              className={`px-4 py-1.5 mono text-[10px] uppercase tracking-[0.25em] font-black border-r border-zinc-800 ${l === lane ? "bg-primary-orange text-black" : "text-zinc-400 hover:text-white"}`}>
              {l} <span className={l === lane ? "text-black/70" : lb ? "text-primary-orange" : "text-zinc-600"}>· {lb ? lb.truck_number : "idle"}</span>
            </button>
          );
        })}
        <div className="ml-auto px-3 mono text-[10px] uppercase tracking-[0.3em] text-primary-orange font-black flex items-center gap-2"><Activity className={`w-3.5 h-3.5 ${scaleFeed.state === "streaming" ? "animate-pulse text-emerald-400" : ""}`} /> Live Batch Weights</div>
      </div>
      )}

      {!batch ? (
        loader ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-5 px-6 text-center" data-testid={`loader-lane-idle-${lane}`}>
            <div className="mono text-sm uppercase tracking-widest text-zinc-500">{lane.replace("-", " ")} is free</div>
            {nextTruck ? (
              <button type="button" onClick={onNextTruck} data-testid={`loader-next-truck-${lane}`}
                className="mono uppercase tracking-wider font-black text-lg px-10 py-6 bg-primary-orange text-black hover:bg-[rgb(var(--primary-hover))] inline-flex items-center gap-3 shadow-[0_0_40px_rgba(245,124,0,.35)]">
                <Truck className="w-6 h-6" /> Next truck · {nextTruck.truck_number} <ArrowRight className="w-6 h-6" />
              </button>
            ) : <div className="mono text-xs uppercase tracking-widest text-zinc-600">No trucks waiting — dispatch will queue the next load</div>}
          </div>
        ) : (
        <div className="flex-1 flex items-center justify-center mono text-[11px] uppercase tracking-widest text-zinc-500 text-center px-6" data-testid="cockpit-lane-idle">{lane} is free — send a waiting truck here from the queue above</div>
        )
      ) : (
        <>
          <div className={`shrink-0 border-b border-zinc-800 px-2 py-1.5 flex items-center gap-2 flex-wrap ${loader ? "order-last border-t bg-black/60 !py-2" : ""}`} data-testid="cockpit-lane-controls">
            {!loader && (
              <>
                <select value={batch.cement_silo_id || ""} onChange={(e) => onSiloChange(e.target.value)} data-testid={`silo-select-${lane}`} className="mono text-[10px] bg-black border border-zinc-700 text-white px-1.5 py-1 uppercase tracking-widest">
                  <option value="">— silo —</option>{SILOS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                {[["Cem open", { action: "open_cem_auger", silo_id: batch.cement_silo_id }, "cem-open"], ["Cem close", { action: "close_cem_auger" }, "cem-close"], ["Gate 1", { action: "open_agg_gate_1" }, "gate1-open"], ["Gate 2", { action: "open_agg_gate_2" }, "gate2-open"], ["Water", { action: "start_water_pump" }, "water"], ["Admix", { action: "dose_admix" }, "admix"]].map(([label, payload, id]) => (
                  <button key={id} type="button" onClick={() => onAction(payload)} data-testid={`btn-${id}-${lane}`} className="mono text-[9px] uppercase tracking-widest px-2 py-1 border border-zinc-700 text-zinc-300 hover:border-primary-orange hover:text-primary-orange">{label}</button>
                ))}
              </>
            )}
            <div className={`ml-auto flex items-center gap-1.5 flex-wrap justify-end ${loader ? "w-full flex gap-2 !flex-nowrap [&>button]:flex-1 [&>button]:justify-center" : ""}`}>
              {overRow && (
                <button type="button" onClick={askRebalance} disabled={rebalBusy} data-testid={`rebalance-mix-${lane}`}
                  title={`${overRow.meta.label} is ${dev(overRow.actual, overRow.target).toFixed(1)}% over — lift every other target to match and hold the mix design`}
                  className={`mono uppercase tracking-widest font-black px-3 py-1.5 border text-[11px] bg-amber-500 border-amber-400 text-black hover:bg-amber-400 animate-pulse inline-flex items-center gap-1 ${BIG}`}>
                  <Scale className={loader ? "w-5 h-5" : "w-3.5 h-3.5"} /> {rebalBusy && !rebal ? "Working…" : "Fix mix"}
                </button>
              )}
              <button type="button" onClick={sendTargets} disabled={pushing || !materials.length} data-testid={`send-targets-${lane}`}
                title="Write these targets into the Rinstrum panel (port 2222)"
                className={`mono text-[10px] uppercase tracking-widest font-black px-2.5 py-1.5 border border-sky-500 text-sky-400 hover:bg-sky-500 hover:text-black disabled:opacity-40 inline-flex items-center gap-1 ${BIG}`}>
                <Upload className={loader ? "w-5 h-5" : "w-3 h-3"} /> {pushing ? "Sending…" : loader ? "Send targets" : "Send targets to scale"}
              </button>
              {(sentLive === batch?.id || loader) && (
                <button type="button" onClick={pressStart} disabled={starting || started === batch?.id || sentLive !== batch?.id} data-testid={`panel-start-${lane}`}
                  title="Press START (F1) on the Rinstrum — begins the loader's on-screen sequence. Only after a verified live write."
                  className={`mono text-[10px] uppercase tracking-widest font-black px-2.5 py-1.5 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-black disabled:opacity-40 inline-flex items-center gap-1 ${BIG}`}>
                  <Play className={loader ? "w-5 h-5" : "w-3 h-3"} /> {started === batch?.id ? "Started" : starting ? "Starting…" : "Start on panel"}
                </button>
              )}
              {lastWrite && ["running", "queued"].includes(lastWrite.status) && (
                <span className="mono text-[9px] uppercase tracking-widest text-amber-300 animate-pulse" data-testid={`scale-write-pending-${lane}`}>writing to panel…</span>
              )}
              <span className={`mono text-[9px] uppercase tracking-widest ${loader ? "hidden" : ""} ${mix?.k412_product ? "text-sky-300" : "text-amber-400"}`} data-testid={`k412-recipe-${lane}`}
                title={mix?.k412_product ? "K412 recipe this mix batches on — the panel is switched to it before targets are written" : "No K412 recipe on this mix — set one in Mix Designs"}>
                {mix?.k412_product ? `K412 · ${mix.k412_product}` : "K412 · no recipe set"}
              </span>
              {lastWrite && lastWrite.status === "failed" && (
                <div className="w-full mt-1 border border-red-500/70 bg-red-500/10 text-red-300 mono text-[10px] px-2 py-1.5 leading-snug" data-testid={`scale-write-failed-${lane}`}>
                  <span className="font-black uppercase tracking-widest">Targets NOT sent · </span>{lastWrite.detail}
                </div>
              )}
              {!loadedAt && (
                <button type="button" onClick={onLoadTruck} data-testid={seq.reached ? `load-truck-${lane}` : `load-truck-manual-${lane}`}
                  title={seq.reached ? "Aggregate bin is at target — discharge into the truck" : "Signal the loader early (bin has not reached target yet)"}
                  className={`mono uppercase tracking-widest font-black px-3 py-1.5 border text-[11px] ${BIG} ${seq.reached
                    ? "bg-emerald-500 border-emerald-400 text-black animate-pulse"
                    : "border-zinc-700 text-zinc-300 hover:border-primary-orange"}`}>▶ Load Truck</button>
              )}
              {loadedAt && (
                // Stays in place once pressed — removing it used to shrink this toolbar and
                // make the whole weights table jump up the screen mid-batch.
                <span className={`mono uppercase tracking-widest font-black px-3 py-1.5 border border-emerald-600/40 text-emerald-500/70 text-[11px] ${BIG} ${loader ? "inline-flex items-center justify-center flex-1" : ""}`} data-testid={`load-truck-done-${lane}`}>✓ Truck Loading</span>
              )}
              {loadComplete && !loader && <div data-testid={`load-complete-${lane}`} className="mono text-[11px] font-black text-emerald-400 border border-emerald-500 px-2 py-1 animate-pulse" title="Bin empty — load discharged to the truck">✓ LOAD COMPLETE</div>}
              <button type="button" onClick={onAbort} data-testid={`e-stop-${lane}`} className={`mono text-[11px] uppercase tracking-widest font-black px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-1 ${BIG}`}><OctagonAlert className={loader ? "w-5 h-5" : "w-3.5 h-3.5"} /> E-Stop</button>
              <button type="button" onClick={() => onComplete?.(false)} data-testid={`complete-load-${lane}`}
                className={`mono text-[11px] uppercase tracking-widest font-black px-3 py-1.5 bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black disabled:bg-zinc-800 disabled:text-zinc-500 disabled:cursor-not-allowed inline-flex items-center gap-1 ${BIG}`}><CheckCircle2 className={loader ? "w-5 h-5" : "w-3.5 h-3.5"} /> Complete Load</button>
              {loader && onGps && (
                <button type="button" onClick={onGps} data-testid={`loader-gps-${lane}`} title="Trucks heading back to the yard"
                  className={`mono uppercase tracking-widest font-black border border-sky-400 text-sky-300 hover:bg-sky-400 hover:text-black inline-flex items-center gap-2 ${BIG}`}><MapPin className="w-5 h-5" /> GPS · Trucks back</button>
              )}
            </div>
          </div>
          {/* Scale headline — the raw number on the weigher, with its countdown to target */}
          {scaleFeed.online && (() => {
            // The agg weigher alternates between NAMED material records and unnamed bin
            // totals (TOTAL/DUMP), so the headline flashed between "what's in the bin"
            // and "what this material is". Pin it: while loading show the material,
            // while discharging show the bin total. Whichever isn't showing is kept so
            // the number never blanks between records.
            const rAgg = scaleFeed.scaleById?.agg;
            if (rAgg && !rAgg.stale && rAgg.weight_kg != null) {
              if (rAgg.material && !isActionReading(rAgg)) aggViewRef.current.named = rAgg;
              else aggViewRef.current.bin = rAgg;
            }
            const { named, bin } = aggViewRef.current;
            const aggShown = loadedAt ? (bin || named) : (named || bin);
            const readingFor = (sid) => (sid === "agg" ? aggShown : scaleFeed.scaleById?.[sid]);
            const groups = [
              { sid: "agg", label: "Agg weigher", target: materials.filter((m) => scaleForKey(m.key) === "agg").reduce((s, m) => s + m.target, 0) },
              { sid: "cement", label: "Cement", target: materials.filter((m) => scaleForKey(m.key) === "cement").reduce((s, m) => s + m.target, 0) },
              { sid: "water", label: "Water", target: materials.filter((m) => scaleForKey(m.key) === "water").reduce((s, m) => s + m.target, 0) },
            ].filter((g) => readingFor(g.sid)?.weight_kg != null)
              .map((g) => ({ ...g, reading: readingFor(g.sid) }))
              // When the panel names the material it is weighing, show that material's target
              // rather than the whole-group total — it zeroes between materials.
              .map((g) => {
                const rr = g.reading;
                // DUMP = the bin emptying into the truck. There is no "target" for a
                // discharge, so show it as such instead of a bogus countdown.
                const mat = materialKeyOf(rr, materials);
                if (isActionReading(rr) || (g.sid === "agg" && !mat)) {
                  return { ...g, target: 0, label: isActionReading(rr) ? `${g.label} · discharging` : `${g.label} · bin total` };
                }
                const matRow = mat && materials.find((m) => m.key === mat);
                // Once a material is locked, counting down against it is meaningless —
                // the residual on the scale isn't "4838 kg to go" on a finished row.
                const row = (matRow && !isLockedRow(matRow) ? matRow : null)
                  || materials.find((m) => scaleForKey(m.key) === g.sid && !isLockedRow(m));
                if (!row && matRow) return { ...g, target: 0, label: `${matRow.meta.label} · done` };
                return row ? { ...g, target: row.target, label: row.meta.label } : g;
              });
            if (!groups.length) return null;
            return (
              <div className="shrink-0 border-b border-zinc-800 grid" style={{ gridTemplateColumns: `repeat(${groups.length}, minmax(0, 1fr))` }} data-testid="cockpit-scale-headline">
                {groups.map((g) => {
                  const r = g.reading;
                  const live = snap(r.weight_kg);
                  const togo = Math.max(0, g.target - live);
                  const p = g.target > 0 ? Math.min(110, (live / g.target) * 100) : 0;
                  const over = g.target > 0 && live > g.target * (1 + tolerance / 100);
                  const rate = rates[g.sid] || 0;
                  const cut = !over && g.target > 0 && rate > 0 && togo > 0 && togo / rate <= CUTOFF_LEAD_S;
                  return (
                    <div key={g.sid} className={`px-3 py-2 border-r border-zinc-800 last:border-r-0 ${cut ? "bg-amber-500/10" : ""}`} data-testid={`cockpit-scale-total-${g.sid}`}>
                      <div className="flex items-baseline gap-2">
                        <span className="mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">{g.label || r.label}</span>
                        <span className={`mono text-[8px] uppercase tracking-widest px-1 ${r.stable ? "text-emerald-400" : "text-amber-400 animate-pulse"}`}>{r.stable ? "stable" : "motion"}</span>
                        {cut && <span className="mono text-[8px] uppercase tracking-widest px-1.5 bg-amber-500 text-black font-black animate-pulse inline-flex items-center gap-1" data-testid={`cockpit-scale-cut-${g.sid}`}><Scissors className="w-2.5 h-2.5" /> close gate</span>}
                      </div>
                      <div className="flex items-baseline gap-2 mt-0.5">
                        <span className={`mono tabular-nums font-black text-2xl leading-none ${over ? "text-red-500" : cut ? "text-amber-300" : "text-white"}`} data-testid={`cockpit-scale-live-${g.sid}`}>{live.toFixed(0)}</span>
                        <span className="mono text-[10px] text-zinc-500">kg</span>
                        {g.target > 0 && (
                          <span className="mono text-[10px] text-zinc-400 ml-auto tabular-nums">
                            target {g.target.toFixed(0)} · <span className={togo > 0 ? "text-primary-orange" : "text-emerald-400"}>{togo > 0 ? `${togo.toFixed(0)} to go` : "reached"}</span>
                          </span>
                        )}
                      </div>
                      <div className="h-1 bg-zinc-800 mt-1.5">
                        <div className={`h-full transition-all duration-300 ${over ? "bg-red-500" : cut ? "bg-amber-400" : "bg-primary-orange"}`} style={{ width: `${Math.max(0, p)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
          {/* Load countdown — total still to weigh, then the wait for the weighers to
              empty into the truck, which is what closes the load off automatically.
              Kept to one compact strip: the cockpit is a zero-scroll screen. */}
          <div className={`shrink-0 flex items-center gap-4 px-3 py-1 border-y border-zinc-800 bg-black/40 ${loader ? "loader-countdown" : ""}`} data-testid="load-countdown">
            <div className="flex items-baseline gap-1.5">
              <span className="mono text-[8px] uppercase tracking-[0.25em] text-zinc-500">Still to weigh</span>
              <span className={`mono tabular-nums font-black text-xl leading-none ${totalToGo > 0 ? "text-primary-orange" : "text-emerald-400"}`} data-testid="countdown-to-go">{Math.round(totalToGo).toLocaleString()}</span>
              <span className="mono text-[9px] text-zinc-500">kg</span>
              <span className="mono text-[9px] text-zinc-600">· {pendingCount} of {materials.length} left</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="mono text-[8px] uppercase tracking-[0.25em] text-zinc-500">Batched</span>
              <span className="mono tabular-nums font-black text-xl leading-none text-white" data-testid="countdown-batched">{Math.round(totalBatched).toLocaleString()}</span>
              <span className="mono text-[9px] text-zinc-600">/ {Math.round(totalTarget).toLocaleString()} kg</span>
            </div>
            <div className="flex items-baseline gap-1.5 ml-auto">
              <span className="mono text-[8px] uppercase tracking-[0.25em] text-zinc-500">On the weighers</span>
              {loadComplete ? (
                <span className="mono text-sm font-black uppercase tracking-widest text-emerald-400" data-testid="countdown-on-scales">Load complete</span>
              ) : (
                <span className="flex items-baseline gap-1.5" data-testid="countdown-on-scales">
                  <span className={`mono tabular-nums font-black text-xl leading-none ${!scaleFeed.online ? "text-zinc-600" : aggDischarged ? "text-emerald-400" : "text-amber-400"}`}>{scaleFeed.online ? Math.round(onScales).toLocaleString() : "—"}</span>
                  <span className="mono text-[9px] text-zinc-500">kg</span>
                  <span className={`mono text-[9px] uppercase tracking-widest ${seq.reached && !aggDischarged ? "text-emerald-400" : "text-zinc-600"}`}>
                    {!scaleFeed.online ? "scale offline"
                      : aggDischarged ? "bin at zero — closing load"
                        : loadedAt ? `waiting for zero · bin ${Math.round(seq.live ?? 0)} kg`
                          : seq.reached ? "at target — press LOAD TRUCK"
                            : "loading aggregate"}
                  </span>
                </span>
              )}
            </div>
          </div>
          {loader && (() => {
            const now = materials.find((m) => !isLockedRow(m) && liveFor(m.key)?.weight_kg != null && !liveFor(m.key)?.stale);
            if (!now) return null;
            const lw = Math.max(eff(now.key, liveFor(now.key).weight_kg), eff(now.key, heldRef.current[now.key] ?? 0), now.actual || 0);
            const toGo = Math.max(0, now.target - lw);
            const pc = Math.min(120, pct(lw, now.target));
            return (
              <div className="loader-now-banner shrink-0" style={{ "--now": now.meta.color }} data-testid={`loader-now-banner-${lane}`}>
                <div className="flex items-center gap-4 px-4 py-2">
                  <div className="mono text-[11px] uppercase tracking-[0.3em] font-black opacity-80">Now loading</div>
                  <div className="heading text-3xl font-black uppercase leading-none" style={{ color: "var(--now)" }}>
                    {BIN_NO[now.key] ? <span className="mono text-sm opacity-70 mr-2">BIN {BIN_NO[now.key]}</span> : null}{now.meta.label}
                  </div>
                  <div className="ml-auto flex items-baseline gap-5 tabular-nums">
                    <div className="text-right"><div className="mono text-[9px] uppercase tracking-widest opacity-70">In bin</div><div className="heading text-4xl font-black leading-none">{lw.toFixed(0)}<span className="text-base ml-1 opacity-60">{now.meta.unit}</span></div></div>
                    <div className="text-right"><div className="mono text-[9px] uppercase tracking-widest opacity-70">To go</div><div className="heading text-4xl font-black leading-none" style={{ color: toGo <= 0 ? "#22C55E" : "rgb(var(--primary))" }}>{toGo.toFixed(0)}</div></div>
                    <div className="text-right"><div className="mono text-[9px] uppercase tracking-widest opacity-70">Target</div><div className="heading text-2xl font-black leading-none opacity-80">{now.target.toFixed(0)}</div></div>
                  </div>
                </div>
                <div className="h-2 bg-black/30 relative"><div className="absolute inset-y-0 left-0 transition-[width] duration-300" style={{ width: `${(pc / 120) * 100}%`, background: pc > 103 ? "#EF4444" : pc >= 97 ? "#22C55E" : "var(--now)" }} /><div className="absolute inset-y-0 border-l-2 border-white/80" style={{ left: `${(100 / 120) * 100}%` }} /></div>
              </div>
            );
          })()}
          <div className={`flex-1 min-h-0 overflow-y-auto ${loader ? "loader-table" : ""}`}>
            <table className="w-full mono text-[11px]" data-testid="cockpit-weights-table">
              <thead className="sticky top-0 bg-zinc-950"><tr className="text-[8px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                <th className="text-left px-2 py-1">Material</th><th className="text-right px-2 py-1">Target</th><th className="text-right px-2 py-1">To go</th><th className="text-right px-2 py-1">Per m³</th><th className="text-right px-2 py-1">Actual{scaleFeed.state === "streaming" ? <span className="text-emerald-400"> · live</span> : ""}</th><th className="px-2 py-1 w-[18%]">Tolerance ±{tolerance}%</th><th className="text-right px-2 py-1">Δ %</th><th className="px-2 py-1 text-center">Accept</th><th className="px-2 py-1 text-right">Set</th>
              </tr></thead>
              <tbody>
                {materials.map((m) => {
                  const open = !!openRef.current[m.key];          // locked under target — still filling
                  const captured = m.actual > 0 && !open;
                  const live = liveFor(m.key);
                  const nowLoading = loader && !captured && live?.weight_kg != null && !live.stale;
                  // Hold the HIGH-WATER mark, not the last reading. The panel zeroes and
                  // counts up again for the next material, so latching the last value made a
                  // finished row fall back to 140 kg and read -92%. The peak is the weight
                  // that actually went in the bin.
                  if (captured) delete heldRef.current[m.key];
                  else if (live?.weight_kg != null) heldRef.current[m.key] = Math.max(heldRef.current[m.key] ?? 0, live.weight_kg);
                  const held = captured ? null : eff(m.key, heldRef.current[m.key] ?? null);
                  const liveW = live?.weight_kg != null ? Math.max(eff(m.key, live.weight_kg), eff(m.key, heldRef.current[m.key] ?? 0)) : null;
                  const streaming = liveW != null;                 // a genuine live reading right now
                  let shown = captured ? m.actual : (streaming ? liveW : held);
                  // An open row never reads BACK below what is already in the bin.
                  if (open && shown != null) shown = Math.max(shown, m.actual || 0);
                  else if (open) shown = m.actual || null;
                  const canAccept = !captured && shown != null;    // live now, or a held reading waiting
                  const d = shown != null ? dev(shown, m.target) : 0;
                  const bad = shown != null && Math.abs(d) > tolerance;
                  const p = shown != null ? Math.min(120, pct(shown, m.target)) : 0;
                  const inTol = shown != null && !bad;
                  const toGo = shown != null ? Math.max(0, m.target - shown) : m.target;
                  const cut = streaming ? cutoffFor(m, shown, captured) : false;   // about to hit target — close the gate now
                  return (
                    <tr key={m.key} className={`border-b border-zinc-800/60 h-[30px] ${nowLoading ? "loader-now-row" : ""} ${cut ? "bg-amber-500/10 ring-1 ring-inset ring-amber-500/60" : ""}`} style={nowLoading ? { "--now": m.meta.color } : undefined} data-testid={`cockpit-mat-row-${m.key}`} data-now-loading={nowLoading || undefined}>
                      <td className="px-2 py-0.5 uppercase tracking-wide font-black text-[10px]" style={{ color: m.meta.color }}>
                        {nowLoading && <span className="loader-now-tag" data-testid={`loader-now-${m.key}`}>▶ LOADING</span>}
                        {open && <span className="mono text-[8px] uppercase tracking-widest px-1 mr-1 bg-amber-500 text-black font-black" title="Still under target — this line stays live for a top-up until it makes weight or you press ACCEPT" data-testid={`cockpit-topup-${m.key}`}>LIVE · + MORE</span>}
                        {BIN_NO[m.key] ? <span className="mono text-[8px] text-zinc-500 mr-1" data-testid={`cockpit-bin-no-${m.key}`}>BIN {BIN_NO[m.key]}</span> : null}
                        {m.meta.label}
                      </td>
                      <td className="px-2 py-0.5 text-right text-zinc-300 tabular-nums">{m.target.toFixed(1)} <span className="text-zinc-600 text-[9px]">{m.meta.unit}</span></td>
                      <td className={`px-2 py-0.5 text-right tabular-nums font-black ${captured ? "text-zinc-600" : cut ? "text-amber-300" : toGo <= 0 ? "text-emerald-400" : "text-primary-orange"}`} data-testid={`cockpit-togo-${m.key}`}>
                        {captured ? "—" : cut ? (
                          <span className="inline-flex items-center gap-1 animate-pulse" data-testid={`cockpit-cutoff-${m.key}`}><Scissors className="w-3 h-3" /> CUT</span>
                        ) : toGo > 0 ? toGo.toFixed(0) : "0"}
                      </td>
                      <td className="px-2 py-0.5 text-right text-zinc-400 tabular-nums" data-testid={`cockpit-perm3-${m.key}`}>
                        {m.perM3 != null ? m.perM3.toFixed(1) : "—"} <span className="text-zinc-600 text-[9px]">{m.meta.unit}</span>
                      </td>
                      <td className="px-2 py-0.5 text-right">
                        <button type="button" data-testid={`cockpit-actual-${m.key}`}
                          onClick={() => setManual(m)}
                          title={captured ? "Locked — click to correct manually" : "Click to type a weight manually"}
                          className={`inline-flex items-center justify-end gap-1 min-w-[96px] px-1.5 py-0.5 border font-black text-sm tabular-nums ${captured ? (bad ? "border-red-500 text-red-400" : "border-emerald-500 text-emerald-400") : canAccept ? (inTol ? "border-emerald-500 text-emerald-300" : "border-amber-500/70 text-amber-300") : "border-zinc-700 text-zinc-500"}`}>
                          {captured ? <Lock className="w-3 h-3" /> : streaming ? <Activity className={`w-3 h-3 ${live.stable ? "" : "animate-pulse"}`} /> : canAccept ? <Activity className="w-3 h-3 opacity-40" /> : null}
                          {shown != null ? shown.toFixed(1) : "—"}
                          {!captured && streaming && !live.stable && <span className="text-[8px] text-amber-400 animate-pulse" title="Still filling — this is not the final weight">&#9662;&#9662;</span>}
                        </button>
                        {captured && streaming && <div className="mono text-[8px] text-zinc-500 leading-none mt-0.5">scale {liveW.toFixed(1)}</div>}
                      </td>
                      <td className="px-2 py-0.5">
                        <div className="relative h-3 bg-zinc-900 border border-zinc-700 overflow-hidden">
                          <div className="absolute inset-y-0 bg-emerald-500/15 border-x border-emerald-500/50" style={{ left: `${((100 - tolerance) / 120) * 100}%`, width: `${((2 * tolerance) / 120) * 100}%` }} />
                          <div className="absolute inset-y-0 left-0 transition-[width] duration-300" style={{ width: `${(p / 120) * 100}%`, background: bad ? "#EF4444" : p > 100 ? "#F59E0B" : captured ? "#22C55E" : "#F57C00" }} />
                          <div className="absolute inset-y-0 border-l-2 border-white/80" style={{ left: `${(100 / 120) * 100}%` }} />
                        </div>
                      </td>
                      <td className={`px-2 py-0.5 text-right tabular-nums font-black ${shown == null ? "text-zinc-600" : bad ? "text-red-400" : "text-emerald-400"}`}>{shown != null ? `${d >= 0 ? "+" : ""}${d.toFixed(1)}%` : "—"}</td>
                      <td className="px-2 py-0.5 text-center">
                        {captured ? (
                          <span className="mono text-[9px] uppercase tracking-widest text-emerald-500 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> locked</span>
                        ) : canAccept ? (
                          <button type="button" data-testid={`cockpit-accept-${m.key}`}
                            onClick={() => accept(m, shown)}
                            title={`Lock ${shown.toFixed(0)} ${m.meta.unit} in as the weighed amount`}
                            className={`mono text-[10px] uppercase tracking-widest font-black px-2.5 py-1 border ${inTol ? "bg-emerald-500 border-emerald-500 text-black hover:bg-emerald-400" : "border-amber-500/70 text-amber-300 hover:border-emerald-500 hover:text-emerald-400"}`}>
                            ✓ Accept
                          </button>
                        ) : (
                          <span className="text-zinc-700">—</span>
                        )}
                      </td>
                      <td className="px-2 py-0.5 text-right"><button type="button" onClick={() => setManual(m)} data-testid={`cockpit-manual-${m.key}`} title="Type a weight" className="p-1 border border-zinc-700 text-zinc-400 hover:border-primary-orange hover:text-primary-orange"><Pencil className="w-3 h-3" /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        </>
      )}
      <ManualCapture open={!!manual} onOpenChange={(o) => !o && setManual(null)} material={manual?.meta} target={manual?.target} initial={manual?.actual} unit={manual?.meta?.unit} onSave={(v) => manual && operatorCapture(manual.key, v)} />
      <AlertDialog open={!!confirmOver} onOpenChange={(o) => !o && setConfirmOver(null)}>
        <AlertDialogContent className="rounded-none bg-surface border-industrial" data-testid="accept-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="heading uppercase flex items-center gap-2 text-amber-400">
              <OctagonAlert className="w-5 h-5" /> Weight outside tolerance
            </AlertDialogTitle>
            <AlertDialogDescription className="mono text-sm text-ink">
              {confirmOver && (
                <>
                  <span className="font-black uppercase tracking-wide" style={{ color: confirmOver.m.meta.color }}>{confirmOver.m.meta.label}</span> is{" "}
                  <span className={`font-black ${confirmOver.dir === "over" ? "text-red-400" : "text-amber-300"}`}>{Math.abs(confirmOver.off).toFixed(1)}% {confirmOver.dir}</span>{" "}
                  target — <span className="tabular-nums font-black text-ink-strong">{confirmOver.value.toFixed(1)}</span> vs{" "}
                  <span className="tabular-nums">{confirmOver.m.target.toFixed(1)} {confirmOver.m.meta.unit}</span> (±{tolerance}% allowed).
                  <br />Lock this weight onto the docket anyway?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none mono uppercase tracking-widest text-xs" data-testid="accept-confirm-cancel">Keep weighing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAcceptNow} className="rounded-none mono uppercase tracking-widest text-xs bg-primary-orange text-black hover:bg-[rgb(var(--primary-hover))]" data-testid="accept-confirm-ok">Accept anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!rebal} onOpenChange={(o) => !o && setRebal(null)}>
        <AlertDialogContent className="rounded-none bg-surface border-industrial max-w-3xl" data-testid="rebalance-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="heading uppercase flex items-center gap-2 text-amber-400">
              <Scale className="w-5 h-5" /> Fix the mix after an over-weigh
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="mono text-sm text-ink">
                {rebal && (
                  <>
                    <div>
                      <span className="font-black uppercase text-ink-strong">{metaFor(rebal.driver_material).label}</span> is{" "}
                      <span className="font-black text-red-400">{rebal.over_pct.toFixed(1)}% over</span> target. Lifting every
                      other target by <span className="font-black text-amber-300">×{rebal.factor.toFixed(3)}</span> keeps the
                      proportions and the water/cement ratio exactly on design.
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 tabular-nums">
                      <span>Docket stays at <span className="font-black text-ink-strong">{rebal.ordered_m3} m³</span></span>
                      <span className="text-zinc-400">Real yield ≈ <span className="font-black text-amber-300">{rebal.yield_m3} m³</span></span>
                      {rebal.truck_capacity_m3 ? <span className="text-muted-industrial">Truck holds {rebal.truck_capacity_m3} m³</span> : null}
                    </div>
                    {rebal.warnings?.map((w) => (
                      <div key={w} className="mt-2 border border-amber-500/70 bg-amber-500/10 text-amber-300 px-2 py-1.5 text-[12px] leading-snug" data-testid="rebalance-warning">⚠ {w}</div>
                    ))}
                    <div className="mt-3 max-h-[42vh] overflow-y-auto border border-zinc-800">
                      <table className="w-full text-[12px]" data-testid="rebalance-rows">
                        <thead className="sticky top-0 bg-zinc-950 text-[9px] uppercase tracking-widest text-muted-industrial">
                          <tr><th className="text-left px-2 py-1">Material</th><th className="text-right px-2 py-1">Target now</th><th className="text-right px-2 py-1">New target</th><th className="text-right px-2 py-1">Weighed</th><th className="text-left px-2 py-1">&nbsp;</th></tr>
                        </thead>
                        <tbody>
                          {rebal.rows.filter((r) => r.new_target > 0).map((r) => {
                            const meta = metaFor(r.material);
                            return (
                              <tr key={r.material} className="border-t border-zinc-800/60 tabular-nums" data-testid={`rebalance-row-${r.material}`}>
                                <td className="px-2 py-1 uppercase font-black" style={{ color: meta.color }}>{meta.label}</td>
                                <td className="px-2 py-1 text-right text-muted-industrial">{r.old_target.toFixed(1)}</td>
                                <td className="px-2 py-1 text-right font-black text-ink-strong">{r.new_target.toFixed(1)} <span className="text-zinc-600 text-[9px]">{meta.unit}</span></td>
                                <td className="px-2 py-1 text-right text-zinc-400">{r.actual != null ? r.actual.toFixed(1) : "—"}</td>
                                <td className="px-2 py-1 text-[10px] uppercase tracking-widest">
                                  {r.needs_top_up ? <span className="text-amber-300 font-black" data-testid={`rebalance-topup-${r.material}`}>+ add {r.add_kg.toFixed(0)}</span>
                                    : r.weighed ? <span className="text-emerald-500">ok</span> : <span className="text-zinc-600">to weigh</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none mono uppercase tracking-widest font-black text-base px-8 py-6 bg-red-600 border-red-500 text-white hover:bg-red-700 hover:text-white" data-testid="rebalance-cancel">✕ Decline</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); applyRebalance(); }} disabled={rebalBusy} className="rounded-none mono uppercase tracking-widest font-black text-base px-8 py-6 bg-emerald-500 text-black hover:bg-emerald-400" data-testid="rebalance-apply">
              {rebalBusy ? "Applying…" : "✓ Accept"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ── BATCH TIMING LOG ─────────────────────────────────────────────────────
   How long each load took to weigh, material by material, so a slow gate or a
   tired valve shows up before it costs a shift. Derived server-side from the
   capture log, median-based so one bad load can't move the baseline. */
const fmtSecs = (s) => (s == null ? "—" : s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`);

export function BatchTimingPanel({ plantId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const loadTimings = useCallback(async () => {
    try {
      const { data } = await api.get(`/batches/timings?limit=25${plantId ? `&plant_id=${plantId}` : ""}`);
      setData(data);
    } catch { /* panel is informational — stay quiet */ }
  }, [plantId]);
  useEffect(() => { loadTimings(); }, [loadTimings]);
  useEffect(() => {
    const h = () => loadTimings();
    window.addEventListener("batcher:refresh", h);
    return () => window.removeEventListener("batcher:refresh", h);
  }, [loadTimings]);

  const timed = (data?.loads || []).filter((l) => l.steps?.length);
  const slowLoads = timed.filter((l) => l.slow_count > 0).length;

  return (
    <div className="shrink-0 bg-zinc-950 border border-zinc-800" data-testid="batch-timing-panel">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="batch-timing-toggle"
        className="w-full flex items-center gap-3 px-3 py-1.5 mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-white">
        <Timer className="w-3.5 h-3.5 text-primary-orange" />
        <span className="font-black text-primary-orange">Batch timing</span>
        <span className="text-zinc-500">
          {timed.length ? <>median load <b className="text-white">{fmtSecs(data.median_total_seconds)}</b> · {timed.length} timed</> : "no timed loads yet — timings record from the next load"}
        </span>
        {slowLoads > 0 && <span className="text-amber-400 font-black" data-testid="batch-timing-slow-count">{slowLoads} with a slow gate</span>}
        <span className="ml-auto text-zinc-500">{open ? "▾ hide" : "▸ show"}</span>
      </button>

      {open && (
        <div className="border-t border-zinc-800 max-h-[240px] overflow-y-auto">
          {!timed.length ? (
            <div className="px-3 py-3 mono text-[10px] uppercase tracking-widest text-zinc-500" data-testid="batch-timing-empty">
              Nothing timed yet. Every load from now on records the gap between each material being accepted.
            </div>
          ) : (
            <table className="w-full mono text-[11px]" data-testid="batch-timing-table">
              <thead><tr className="text-[8px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                <th className="text-left px-2 py-1">Truck</th><th className="text-left px-2 py-1">Mix</th><th className="text-right px-2 py-1">m³</th>
                <th className="text-right px-2 py-1">Total</th><th className="text-right px-2 py-1">Slow gates</th><th className="text-right px-2 py-1">Finished</th><th className="px-2 py-1" />
              </tr></thead>
              <tbody>
                {timed.map((l) => (
                  <Fragment key={l.batch_id}>
                    <tr onClick={() => setExpanded((e) => (e === l.batch_id ? null : l.batch_id))}
                      data-testid={`batch-timing-row-${l.batch_id}`}
                      className="border-b border-zinc-800/60 cursor-pointer hover:bg-zinc-900/60">
                      <td className="px-2 py-1 font-black text-white">{l.truck_number || "—"}</td>
                      <td className="px-2 py-1 text-zinc-400">{l.mix_code || "—"}</td>
                      <td className="px-2 py-1 text-right text-zinc-400 tabular-nums">{l.quantity_m3 ?? "—"}</td>
                      <td className="px-2 py-1 text-right font-black tabular-nums text-white">{fmtSecs(l.total_seconds)}</td>
                      <td className={`px-2 py-1 text-right font-black tabular-nums ${l.slow_count ? "text-amber-400" : "text-zinc-600"}`}>{l.slow_count || "—"}</td>
                      <td className="px-2 py-1 text-right text-zinc-500">{l.completed_at ? new Date(l.completed_at).toLocaleTimeString() : "—"}</td>
                      <td className="px-2 py-1 text-right text-zinc-500">{expanded === l.batch_id ? "▾" : "▸"}</td>
                    </tr>
                    {expanded === l.batch_id && (
                      <tr className="border-b border-zinc-800 bg-black/40">
                        <td colSpan={7} className="px-3 py-2">
                          <div className="grid gap-1" data-testid={`batch-timing-steps-${l.batch_id}`}>
                            {l.steps.map((s) => {
                              const w = s.normal_seconds ? Math.min(100, (s.seconds / (s.normal_seconds * 2)) * 100) : 50;
                              return (
                                <div key={s.material} className="flex items-center gap-2">
                                  <span className="mono text-[9px] uppercase tracking-widest w-24 shrink-0" style={{ color: metaFor(s.material).color }}>{metaFor(s.material).label}</span>
                                  <div className="flex-1 h-2 bg-zinc-900 border border-zinc-800 overflow-hidden">
                                    <div className={`h-full ${s.slow ? "bg-amber-500" : "bg-emerald-600"}`} style={{ width: `${w}%` }} />
                                  </div>
                                  <span className={`mono text-[10px] tabular-nums font-black w-14 text-right ${s.slow ? "text-amber-400" : "text-zinc-300"}`}>{fmtSecs(s.seconds)}</span>
                                  <span className="mono text-[9px] text-zinc-600 w-28 text-right">normal {fmtSecs(s.normal_seconds)}{s.slow ? " · slow" : ""}</span>
                                </div>
                              );
                            })}
                          </div>
                          <div className="mono text-[9px] uppercase tracking-widest text-zinc-600 mt-1.5">Time from the previous material being accepted · slow = over {data.slow_factor}× normal</div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
